import { useState, useEffect, useRef } from "react";
import ImportView from "./ImportView.jsx";
import { groupNum } from "./money.js";
import { BusinessTiles, BusinessDetailsView, PLView, MileageView, HoursView, RecurringInvoicesView } from "./BusinessViews.jsx";
import { InvoiceReview } from "./InvoiceReview.jsx";
import { PayslipsView, PaymentsView } from "./ProViews.jsx";
import { TaxHelp } from "./TaxIntl.jsx";
import { CountrySheet } from "./CountrySheet.jsx";
import { COUNTRIES, SUPPORTED_COUNTRY } from "./countries.js";
import { PublicInvoice } from "./PublicInvoice.jsx";
import { remote, invoiceLink } from "./remote.js";
import { useAppearance } from "./appearance.js";
import { generateRecurringInvoices, invoiceTotals, nextInvoiceNumber as nextInvoiceNumberFor, addDays, advance, localToday, buildInvoiceEmail, mailtoLink, effectiveStatus, daysOverdue, applyPayment, removePayment, invoiceBalance, invoicePaidAmount, isPartiallyPaid } from "./biz.js";
import { storage } from "./storage.js";
import { initAnalytics, track, trackInstall } from "./analytics.js";
import { syncConfigured, supabase, configWarning } from "./supabaseClient.js";
import { signUp, signIn, signOut, getSession, pullCloudData, pushCloudData, sendPasswordReset, remoteChanged, hasStamp, subscribeToRemoteChanges, enableEncryption, unlockEncryption, disableEncryption, forgetEncryptionKey, deleteAccountOnServer } from "./sync.js";
import { lockEnabled, lockSettings } from "./appLock.js";
import LockScreen from "./LockScreen.jsx";
import ResetPasswordModal from "./ResetPasswordModal.jsx";
import ForgotPassphraseModal from "./ForgotPassphraseModal.jsx";
import LegalModal, { LegalPage } from "./legal/LegalModal.jsx";
import { consentRecord } from "./legal/legalConfig.js";
import { recordBackup, takeBackupReminder } from "./backupReminder.js";
import OldLinkBanner from "./OldLinkBanner.jsx";
import { passwordChecks, passwordProblem } from "./passwordRules.js";
import LockSettings from "./LockSettings.jsx";
import EncryptionCard from "./EncryptionCard.jsx";
import UnlockSyncModal from "./UnlockSyncModal.jsx";
import DeleteAccountModal from "./DeleteAccountModal.jsx";
import InvestPage from "./invest/InvestPage.jsx";
import { pdfFile, safeFilename, printPdf } from "./deliver.js";
import AppUpdateCard from "./AppUpdateCard.jsx";
import AssistantFab from "./AssistantFab.jsx";
import PrivacyView from "./PrivacyView.jsx";
import BillsSection from "./BillsSection.jsx";
import ReceiptScan from "./ReceiptScan.jsx";
import {
  Lock, WifiOff, Check as CheckIcon, Circle, Plus, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowLeft, Trash2, Download,
  Users, Briefcase, User, Settings as SettingsIcon, Wallet, Check, TrendingUp,
  Lightbulb, Pencil, SlidersHorizontal, Tag, Repeat, Target, Upload, Search, Cloud, UserCircle, LogOut,
  LineChart, RefreshCw, Coins, Smartphone, BookOpen, Share, FileText, Printer, Receipt,
} from "lucide-react";

const CHIP_COLORS = ["#D9B26B", "#3F7C63", "#A23B52", "#4A6FA5", "#7E5C9B"];
// dark text on light chips, light text on dark ones — works in both themes
function readableOn(hex) {
  const n = parseInt(String(hex).replace("#", ""), 16) || 0;
  const l = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return l > 0.6 ? "#0F0B14" : "#F8F3E8";
}
const PERSONAL_CATEGORIES = ["Groceries", "Dining", "Transport", "Housing", "Utilities", "Shopping", "Health", "Entertainment", "Other"];
const BUSINESS_CATEGORIES = ["Software", "Travel", "Meals & Entertainment", "Office Supplies", "Contractors", "Marketing", "Other"];
const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "INR", symbol: "₹", label: "Indian Rupee" },
  { code: "JPY", symbol: "¥", label: "Japanese Yen" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
  { code: "SGD", symbol: "S$", label: "Singapore Dollar" },
  { code: "AED", symbol: "AED ", label: "UAE Dirham" },
  { code: "NZD", symbol: "NZ$", label: "New Zealand Dollar" },
];

const FIELD_TYPES = [
  { id: "text", label: "Text" },
  { id: "number", label: "Number" },
  { id: "yesno", label: "Yes / No" },
  { id: "choice", label: "Choice" },
];
const TIPS = [
  // Budgeting
  { topic: "Budgeting", title: "Write it down first", body: "Logging an expense the moment you make it is the habit most linked to sticking with a budget \u2014 memory alone tends to undercount small, frequent purchases." },
  { topic: "Budgeting", title: "Give every dollar a job", body: "At the start of the month, assign your income to spending, saving or paying down debt until nothing is left unplanned. A plan beats a hope." },
  { topic: "Budgeting", title: "Try 50 / 30 / 20 as a starting point", body: "A common rule of thumb: about half of income to needs, 30% to wants, 20% to saving and debt. Adjust the split to fit your life, not the other way round." },
  { topic: "Budgeting", title: "Review, don't obsess", body: "A calm monthly check-in on spending and savings tends to build better habits than checking anxiously every day." },
  { topic: "Budgeting", title: "Keep a little fun money", body: "A budget with no room for enjoyment usually breaks. Set aside a guilt-free amount each month so the plan is one you can actually live with." },
  { topic: "Budgeting", title: "Start with your biggest categories", body: "Most savings come from the top two or three categories, not from the small ones. Look at those first in Insights." },
  { topic: "Budgeting", title: "Wait 48 hours on big purchases", body: "A short pause cuts impulse buying. If you still want it two days later and it fits the budget, buy it without the guilt." },
  { topic: "Budgeting", title: "Spread out the irregular bills", body: "Divide yearly and quarterly bills by 12 or 3 and set that amount aside each month, so a large bill never arrives as a surprise." },
  { topic: "Budgeting", title: "Name the shop in the note", body: "Typing the shop or provider in an entry's note helps the app spot repeat bills and merchants, and flag when one costs more than it used to." },
  { topic: "Budgeting", title: "Small leaks sink big budgets", body: "Recurring subscriptions and small daily purchases rarely feel significant alone, but they add up over a year. A monthly review catches drift early." },
  { topic: "Budgeting", title: "Compare your bills once a year", body: "Phone, internet, insurance and energy prices drift up, and loyalty rarely earns the best rate. Check what new customers pay, then ask your provider to match it." },
  { topic: "Budgeting", title: "Set a weekly money minute", body: "Ten minutes on the same day each week to log stragglers and glance at your budget keeps the monthly review short and calm." },
  // Saving
  { topic: "Saving", title: "Pay yourself first", body: "Move a fixed amount to savings right after you're paid, before spending on anything else, so saving isn't just whatever happens to be left over." },
  { topic: "Saving", title: "Automate the good habits", body: "Set up automatic transfers for savings and regular bills. Doing the right thing by default is easier than relying on willpower every month." },
  { topic: "Saving", title: "Start your cushion small", body: "One month of essential expenses is a solid first target. Build towards 3\u20136 months once that feels comfortable." },
  { topic: "Saving", title: "Know your why", body: "A specific goal \u2014 a trip, a deposit, a safety net \u2014 makes it easier to keep saving than a vague goal like \u201csave more.\u201d" },
  { topic: "Saving", title: "Match savings to timeframe", body: "Money you'll need soon belongs somewhere stable. Money you won't touch for years can typically handle more short-term ups and downs." },
  { topic: "Saving", title: "Pay off expensive debt first", body: "Putting extra money towards the debt with the highest interest rate usually saves the most. Clearing the smallest balance first can be a good motivator if you need quick wins." },
  // Investing
  { topic: "Investing", title: "Build a cushion before you invest", body: "Most planners suggest keeping 3\u20136 months of essential expenses somewhere easy to access before putting money into anything less liquid." },
  { topic: "Investing", title: "Time in the market beats timing it", body: "Investing consistently over a long horizon has historically mattered more than trying to guess the perfect moment to buy or sell." },
  { topic: "Investing", title: "Diversify, don't concentrate", body: "Spreading money across different assets reduces how much a single bad outcome can hurt your overall position." },
  { topic: "Investing", title: "Watch the fees", body: "Small annual fees compound against you just as returns compound for you. Know what you're paying before you commit." },
  // Privacy and safety
  { topic: "Privacy", title: "Back up before you change phones", body: "Your entries live on this device. Use Back up in Settings before you switch phones, clear your browser data or reinstall the app." },
  { topic: "Privacy", title: "Use a unique password for sync", body: "If you turn on Account & sync, use a long password you don't use anywhere else. A password manager makes that painless." },
  { topic: "Privacy", title: "Nobody legitimate asks for your codes", body: "Banks and apps never need your PIN, your password or the one-time code texted to you. Anyone who asks is a scammer, however official they sound." },
  { topic: "Privacy", title: "Think before you share a screenshot", body: "Balances, account numbers and names are easy to miss in a screenshot. Crop or hide them before you send it to anyone." },
  // Business
  { topic: "Business", biz: true, title: "Keep business and personal money apart", body: "A separate account or card makes your records cleaner and your year-end far less painful." },
  { topic: "Business", biz: true, title: "Invoice promptly", body: "The sooner you send an invoice, the sooner you're paid. Put a clear due date on it and say how to pay." },
  { topic: "Business", biz: true, title: "Follow up on late invoices early", body: "A friendly reminder a few days after the due date resolves most late payments without any awkwardness." },
  { topic: "Business", biz: true, title: "Keep receipts as you go", body: "Take a photo of each receipt the day you get it. Chasing them at year end means some always go missing." },
  { topic: "Business", biz: true, title: "Set money aside as income arrives", body: "Move a share of each payment into a separate account for tax and slow months, so a big bill never has to come out of operating cash." },
];

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

const PROFILE_META = {
  personal: { icon: User, label: "Personal", accent: "#7B9BC9", desc: "Track your own spending by category. Simple tally, monthly budget." },
  household: { icon: Users, label: "Household", accent: "#6FAE8E", desc: "Add the people you live with. See who spent what, split fairly." },
  business: { icon: Briefcase, label: "Business", accent: "#D9B26B", desc: "Tag clients and projects. Mark what's deductible. Export for taxes." },
};

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function categoryColor(cat) {
  let sum = 0;
  for (let i = 0; i < cat.length; i++) sum += cat.charCodeAt(i);
  return CHIP_COLORS[sum % CHIP_COLORS.length];
}

function getCategories(profile) {
  if (profile?.categories && profile.categories.length) return profile.categories;
  return profile?.type === "business" ? BUSINESS_CATEGORIES : PERSONAL_CATEGORIES;
}

function businessDefaults() {
  return { business: {}, mileage: [], staff: [], hours: [], income: [], recurringInvoices: [] };
}

function defaultCategoriesFor(type) {
  return type === "business" ? [...BUSINESS_CATEGORIES] : [...PERSONAL_CATEGORIES];
}

function isThisMonth(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function isThisYear(dateStr) {
  return new Date(dateStr).getFullYear() === new Date().getFullYear();
}

function isInMonth(dateStr, year, month) {
  const d = new Date(dateStr);
  return d.getFullYear() === year && d.getMonth() === month;
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const APP_CSS = `
        .vellum-app { --ink:#0F0B14; --card:#1B1420; --card2:#251C2C; --ivory:#F4E9D8; --ivory-dim:rgba(244,233,216,0.58); --brass:#D9B26B; --brass-hi:#F0D9A0; --brass-lo:#A9793B; --brass-dim:rgba(217,178,107,0.16); --sage:#3F8368; --rust:#B5445F; --sapphire:#5B7FB5; --amethyst:#8F6BAE; --hair:rgba(217,178,107,0.14); --page:#0B120F; --on-brass:#0F0B14; --chip:rgba(244,233,216,0.08); --ring:rgba(244,233,216,0.3); --grid:rgba(244,233,216,0.25); --hero-a:#241A2C; --hero-b:#180F1E; --hero-edge:rgba(255,255,255,0.06); --backdrop:rgba(8,5,11,0.78); --gold-glow:rgba(169,121,59,0.28); --sent:#7E97A6; --card-shadow:none; font-family:'Inter Variable','Inter',sans-serif; }
        :root[data-theme="light"] .vellum-app { --page:#E9E0CE; --ink:#F7F1E6; --card:#FFFDF8; --card2:#EFE6D3; --ivory:#21172A; --ivory-dim:rgba(33,23,42,0.66); --brass:#84601E; --brass-hi:#E8CB8C; --brass-lo:#B98A3E; --brass-dim:rgba(132,96,30,0.12); --sage:#2E7355; --rust:#A83A55; --sapphire:#456A9E; --amethyst:#7B5799; --hair:rgba(132,96,30,0.20); --chip:rgba(33,23,42,0.07); --ring:rgba(33,23,42,0.35); --grid:rgba(33,23,42,0.30); --hero-a:#FFFFFF; --hero-b:#F4EAD3; --hero-edge:rgba(132,96,30,0.18); --backdrop:rgba(33,23,42,0.45); --gold-glow:rgba(169,121,59,0.22); --sent:#47647A; --card-shadow:0 1px 2px rgba(60,40,10,0.07), 0 1px 1px rgba(60,40,10,0.04); }
        .v-display { font-family:'Fraunces Variable','Fraunces',Georgia,serif; }
        .v-mono { font-family:'Inter Variable','Inter',system-ui,sans-serif; font-variant-numeric:tabular-nums; font-feature-settings:'zero' 0; }
        .bg-ink{background-color:var(--ink);} .bg-card{background-color:var(--card); box-shadow:var(--card-shadow);} .bg-card2{background-color:var(--card2);}
        .bg-brass{background-color:var(--brass);} .bg-brass-dim{background-color:var(--brass-dim);}
        .bg-gold-grad{background:linear-gradient(135deg, var(--brass-hi), var(--brass-lo)); box-shadow:0 6px 18px var(--gold-glow);}
        .card-hero{background:linear-gradient(165deg, var(--hero-a) 0%, var(--hero-b) 100%); border-top:1px solid var(--hero-edge); box-shadow:var(--card-shadow);}
        .text-ivory{color:var(--ivory);} .text-ivory-dim{color:var(--ivory-dim);} .text-brass{color:var(--brass);}
        .text-sage{color:var(--sage);} .text-rust{color:var(--rust);}
        .text-ink{color:var(--on-brass);} .bg-brass.text-ink{color:var(--ink);}
        .border-hair{border-color:var(--hair);}
        .ledger-row{border-bottom:1px solid var(--hair);}
        .stamp{border:1.5px dashed var(--brass); background:var(--brass-dim); border-radius:9999px; transform:rotate(-6deg); box-shadow:0 0 22px rgba(217,178,107,0.14);}
        .ledger-bg{background-image:repeating-linear-gradient(to bottom, transparent, transparent 26px, var(--hair) 27px);}
        :root[data-theme="light"] .ledger-bg{background-image:repeating-linear-gradient(to bottom, transparent, transparent 26px, rgba(132,96,30,0.09) 27px);}
        .overlay-backdrop{background:var(--backdrop);}
        .gold-rule{width:44px; height:2px; background:linear-gradient(90deg, transparent, var(--brass), transparent);}
        input, textarea, select, .v-input { background:var(--card2); color:var(--ivory); border:1px solid var(--hair); }
        input::placeholder, textarea::placeholder { color:var(--ivory-dim); }
        input:focus, textarea:focus, select:focus { outline:none; border-color:var(--brass); }
        button:focus-visible, a:focus-visible, [role="radio"]:focus-visible { outline:2px solid var(--brass); outline-offset:2px; }
        .tab-label { font-size:min(0.75rem, 3vw); }
        .tab-brand { display:none; }
        @media (min-width:1024px) {
          .has-nav { padding-left:15rem; }
          .tabbar { left:max(0px, calc((100vw - 72rem) / 2)) !important; right:auto !important; top:0; bottom:0; width:15rem; border-top:0 !important; border-right:1px solid var(--hair); padding:1.5rem 0.75rem; overflow-y:auto; }
          .tabbar-inner { max-width:none !important; flex-direction:column; align-items:stretch !important; gap:0.25rem; }
          .tab-brand { display:flex; align-items:center; gap:0.625rem; padding:0 0.5rem 1.25rem; }
          .tabbtn { flex-direction:row !important; justify-content:flex-start; gap:0.75rem !important; padding:0.625rem 0.875rem !important; border-radius:0.75rem; flex:none !important; }
          .tabbtn[data-active="true"] { background:var(--brass-dim); }
          .tab-label { font-size:0.875rem; }
          .app-shell > div.pt-8.pb-28:not(.wide-cols) { max-width:44rem; margin-inline:auto; }
          .wide-cols, .wide-flow { column-count:2; column-gap:2.5rem; }
          .wide-cols > *, .wide-flow > * { break-inside:avoid; }
          .wide-cols > :first-child { column-span:all; }
          .wide-flow > .mt-7 { margin-top:0; padding-top:1.5rem; }
        }
        @media print {
          body * { visibility: hidden; }
          #invoice-print-area, #invoice-print-area * { visibility: visible; }
          #invoice-print-area { position: absolute; left: 0; top: 0; width: 100%; background: white; color: black; padding: 24px; }
          #invoice-print-area .v-mono { font-family: Inter, Arial, sans-serif; font-variant-numeric: tabular-nums; }
          @page { margin: 12mm; }
          #invoice-print-area { border-radius: 0 !important; box-shadow: none !important; }
          .overlay-backdrop, .overlay-backdrop > div { position: static !important; overflow: visible !important; max-height: none !important; background: none !important; display: block !important; padding: 0 !important; }
        }
      `;

function Seg({ value, options, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map(([id, text]) => (
        <button key={id} role="radio" aria-checked={value === id} onClick={() => onChange(id)} className="rounded-lg py-2 text-xs font-medium"
          style={{ background: value === id ? "var(--brass)" : "var(--card2)", color: value === id ? "var(--ink)" : "var(--ivory-dim)" }}>{text}</button>
      ))}
    </div>
  );
}

function lerpColor(hexA, hexB, t) {
  const a = parseInt(hexA.slice(1), 16);
  const b = parseInt(hexB.slice(1), 16);
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

function heatColor(value, min, max) {
  if (max <= min) return "#3F8368";
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return lerpColor("#3F8368", "#B5445F", t);
}

function dayOfYear(d) {
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.floor((d - start) / 86400000) + 1;
}

function getWeekRange(ref) {
  const d = new Date(ref);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return [monday, sunday];
}

function isInRange(dateStr, start, end) {
  const d = new Date(dateStr);
  return d >= start && d <= end;
}

function formatShort(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function categoryBreakdown(expenses) {
  const map = {};
  expenses.forEach((e) => { map[e.category] = (map[e.category] || 0) + e.amount; });
  const total = Object.values(map).reduce((s, v) => s + v, 0);
  return Object.entries(map)
    .map(([category, amount]) => ({ category, amount, pct: total > 0 ? (amount / total) * 100 : 0 }))
    .sort((a, b) => b.amount - a.amount);
}

function memberBreakdown(expenses, members) {
  const map = {};
  expenses.forEach((e) => {
    const key = e.member === "shared" ? "shared" : e.member;
    map[key] = (map[key] || 0) + e.amount;
  });
  const total = Object.values(map).reduce((s, v) => s + v, 0);
  return Object.entries(map)
    .map(([key, amount]) => {
      const m = members.find((mm) => mm.id === key);
      return {
        key,
        name: key === "shared" ? "Shared" : m?.name || "Unknown",
        color: key === "shared" ? "#4A6FA5" : m?.color || "#4A6FA5",
        amount,
        pct: total > 0 ? (amount / total) * 100 : 0,
      };
    })
    .sort((a, b) => b.amount - a.amount);
}

function fmt(n) {
  return `$${groupNum(n, "USD")}`;
}

function todayStr() {
  return localToday();
}

function AppMain() {
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState("onboarding");
  const [loaded, setLoaded] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [editingBudget, setEditingBudget] = useState(false);
  const [period, setPeriod] = useState("week");
  const [weekAnchor, setWeekAnchor] = useState(todayStr());
  const [monthAnchor, setMonthAnchor] = useState(todayStr());
  const [yearAnchor, setYearAnchor] = useState(todayStr());
  const [selectedDay, setSelectedDay] = useState(null);
  const [chronCat, setChronCat] = useState(null); // category expanded in Chronicle's breakdown

  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(todayStr());
  const [assignMember, setAssignMember] = useState("shared");
  const [assignClient, setAssignClient] = useState("none");
  const [deductible, setDeductible] = useState(true);
  const [editingExpenseId, setEditingExpenseId] = useState(null);

  const [memberName, setMemberName] = useState("");
  const [clientSheet, setClientSheet] = useState(null); // { id|null, name, email, phone, address } while the client sheet is open

  const [customValues, setCustomValues] = useState({});
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [fieldOptions, setFieldOptions] = useState("");
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [tipDismissed, setTipDismissed] = useState(false);
  const [tipSeed] = useState(() => Math.random());
  const [categoryDraft, setCategoryDraft] = useState("");
  const [editingCategoryOriginal, setEditingCategoryOriginal] = useState(null);

  const [recurringAmount, setRecurringAmount] = useState("");
  const [recurringCategory, setRecurringCategory] = useState("");
  const [recurringNote, setRecurringNote] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("monthly");
  const [recurringStartDate, setRecurringStartDate] = useState(todayStr());
  const [editingRecurringId, setEditingRecurringId] = useState(null);

  const [goalName, setGoalName] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [goalDate, setGoalDate] = useState("");
  const [contributionDrafts, setContributionDrafts] = useState({});

  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [installBannerDismissed, setInstallBannerDismissed] = useState(true);

  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [invoiceClientId, setInvoiceClientId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceIssueDate, setInvoiceIssueDate] = useState(todayStr());
  const [invoiceDueDate, setInvoiceDueDate] = useState(todayStr());
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [invoiceItems, setInvoiceItems] = useState([{ id: uid(), activity: "", description: "", qty: "1", rate: "" }]);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const [reviewDraft, setReviewDraft] = useState(null);
  const [serverCfg, setServerCfg] = useState(null);
  const [showCountry, setShowCountry] = useState(false);
  const [countrySkipped, setCountrySkipped] = useState(false);
  const [stripeInfo, setStripeInfo] = useState({ connected: false, chargesEnabled: false });
  const quickPrintRef = useRef(false);
  const { prefs, setPrefs, theme, effectiveScale } = useAppearance();
  const [invoiceTaxRate, setInvoiceTaxRate] = useState("");
  const [invoiceBillTo, setInvoiceBillTo] = useState("");
  const [invoiceRepeat, setInvoiceRepeat] = useState("none");
  const [invoiceHourIds, setInvoiceHourIds] = useState([]);

  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerCategoryFilter, setLedgerCategoryFilter] = useState("All");

  const [session, setSession] = useState(null);
  const [authMode, setAuthMode] = useState("login");   // login | signup | forgot
  const [authInfo, setAuthInfo] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authPhone, setAuthPhone] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState("idle");
  const [lastSynced, setLastSynced] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [authAgree, setAuthAgree] = useState(false);         // ticked "I agree to the Terms and Privacy Policy" on the sign-up form
  const [legalDoc, setLegalDoc] = useState(null);            // { doc: "privacy" | "terms", full: boolean } while a legal document is open
  const [showForgotPass, setShowForgotPass] = useState(false);     // choosing a new password after the emailed link
  const dirtyRef = useRef(false);                        // this device has changes the cloud doesn't have yet
  const skipPushRef = useRef(false);                     // the next state change just came FROM the cloud: don't send it back
  const lastPushedJsonRef = useRef("");
  const syncBusyRef = useRef(false);
  const syncNowRef = useRef(() => {});
  const [encOn, setEncOn] = useState(false);            // the online copy is end-to-end encrypted
  const [syncLock, setSyncLock] = useState(null);       // { envelope, mode } while this device hasn't been unlocked
  const [syncPrompt, setSyncPrompt] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [lockOn, setLockOn] = useState(() => lockEnabled());      // app lock is set up
  const [appLocked, setAppLocked] = useState(() => lockEnabled());  // and the app is locked right now (starts locked)
  const openSyncLock = (envelope, mode) => { setSyncLock({ envelope, mode }); setSyncPrompt(true); };
  const [accountPlan, setAccountPlan] = useState("free");
  const localSnapshotRef = useRef({ profiles: [], activeId: null });

  useEffect(() => {
    initAnalytics();
    track("app_opened");
    (async () => {
      let localData = null;
      try {
        const res = await storage.get("vellum-data");
        if (res && res.value) localData = JSON.parse(res.value);
      } catch (e) {
        // no local data yet
      }

      const applyProfiles = (rawProfiles, activeIdVal) => {
        const normalized = rawProfiles.map((p) => ({ customFields: [], categories: defaultCategoriesFor(p.type), categoryBudgets: {}, recurring: [], goals: [], investments: [], watchlist: [], invoices: [], ...businessDefaults(), currency: "USD", ...p }));
        setProfiles(normalized);
        setActiveId(activeIdVal || normalized[0].id);
        setView("dashboard");
      };

      if (syncConfigured) {
        const restoredSession = await getSession();
        setSession(restoredSession);
        if (restoredSession) {
          const cloud = await pullCloudData(restoredSession.user.id);
          if (cloud?.plan) setAccountPlan(cloud.plan);
          if (cloud?.locked) {
            // encrypted online copy and this device has no key yet: work from local data until it's unlocked
            setEncOn(true);
            openSyncLock(cloud.envelope, "startup");
            if (localData?.profiles?.length) applyProfiles(localData.profiles, localData.activeId);
          } else if (cloud?.data?.profiles?.length) {
            if (cloud.encrypted) setEncOn(true);
            skipPushRef.current = true;
            applyProfiles(cloud.data.profiles, cloud.data.activeId);
          } else if (localData?.profiles?.length) {
            applyProfiles(localData.profiles, localData.activeId);
            await pushCloudData(restoredSession.user.id, localData);
          }
          setLoaded(true);
          return;
        }
      }

      if (localData?.profiles?.length) applyProfiles(localData.profiles, localData.activeId);
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    localSnapshotRef.current = { profiles, activeId };
  }, [profiles, activeId]);

  useEffect(() => {
    if (!loaded || session || !lockOn) return;           // only people with no account and an app lock
    if (takeBackupReminder()) showToast("Reminder: you have no online backup. Download a backup file from Settings.");
  }, [loaded]);

  useEffect(() => {
    if (!lockOn) return;
    let hiddenAt = null;
    const onVis = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      if (hiddenAt != null) {
        const cfg = lockSettings();
        if (cfg && Date.now() - hiddenAt >= (cfg.autoLockSec || 0) * 1000) setAppLocked(true);
        hiddenAt = null;
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [lockOn]);

  useEffect(() => {
    if (!syncConfigured) return;
    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (event === "PASSWORD_RECOVERY") setShowReset(true);   // they opened the reset link from their email
      if (event === "SIGNED_OUT") showToast("Signed out — data stays on this device");
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    let addedCount = 0;
    const updated = profiles.map((p) => {
      if (!p.recurring || p.recurring.length === 0) return p;
      let expenses = [...p.expenses];
      const recurring = p.recurring.map((r) => {
        let next = r.nextDate;
        let guard = 0;
        while (next <= todayStr() && guard < 36) {
          expenses = [{ id: uid(), amount: r.amount, category: r.category, note: r.note || "Recurring", date: next, member: p.type === "household" ? "shared" : null, clientId: p.type === "business" ? "none" : null, deductible: p.type === "business" ? true : null, custom: {} }, ...expenses];
          addedCount++;
          const d = new Date(next);
          if (r.frequency === "weekly") d.setDate(d.getDate() + 7); else d.setMonth(d.getMonth() + 1);
          next = toISODate(d);
          guard++;
        }
        return { ...r, nextDate: next };
      });
      return { ...p, expenses, recurring };
    });
    if (addedCount > 0) {
      setProfiles(updated);
      showToast(`Added ${addedCount} recurring ${addedCount === 1 ? "entry" : "entries"}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    const done = () => { if (quickPrintRef.current) { quickPrintRef.current = false; setViewingInvoiceId(null); } };
    window.addEventListener("afterprint", done);
    return () => window.removeEventListener("afterprint", done);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    let made = 0;
    const updated = profiles.map((p) => {
      const r = generateRecurringInvoices(p);
      made += r.count;
      return r.profile;
    });
    if (made > 0) {
      setProfiles(updated);
      showToast(`Created ${made} recurring ${made === 1 ? "invoice" : "invoices"} (drafts)`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    const onInstalled = () => {
      trackInstall();
      setIsStandalone(true);
      setInstallPromptEvent(null);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  useEffect(() => {
    // Chrome/Edge/Android hold off on offering to install until they think
    // it's worth it, then fire this event instead of showing their own UI —
    // capturing it is what lets us show our own "Install" button instead of
    // relying on the browser's own timing, which is inconsistent enough
    // that people can go a while without ever seeing it.
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    setIsStandalone(standalone);
    setIsIOSDevice(/iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream);

    (async () => {
      try {
        const res = await storage.get("install-banner-dismissed");
        setInstallBannerDismissed(Boolean(res?.value));
      } catch (e) {
        setInstallBannerDismissed(false);
      }
    })();

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  async function triggerInstall() {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    setInstallPromptEvent(null);
    track(choice.outcome === "accepted" ? "install_prompt_accepted" : "install_prompt_dismissed");
  }

  async function dismissInstallBanner() {
    setInstallBannerDismissed(true);
    try {
      await storage.set("install-banner-dismissed", "1");
    } catch (e) {
      // non-critical
    }
  }

  useEffect(() => {
    if (!loaded) return;
    (async () => {
      const json = JSON.stringify({ profiles, activeId });
      try { await storage.set("vellum-data", json); } catch (e) { /* ignore save errors in prototype */ }
      if (skipPushRef.current) { skipPushRef.current = false; lastPushedJsonRef.current = json; return; }   // these changes just arrived from the cloud
      if (syncConfigured && session && !syncLock) {
        if (json === lastPushedJsonRef.current) return;      // nothing new to send
        dirtyRef.current = true;
        setSyncStatus("syncing");
        syncBusyRef.current = true;          // a background check shouldn't run while this upload is in flight
        let ok;
        try { ok = await pushCloudData(session.user.id, { profiles, activeId }); } finally { syncBusyRef.current = false; }
        if (ok === true) { lastPushedJsonRef.current = json; dirtyRef.current = false; setLastSynced(new Date()); setSyncStatus("synced"); }
        else if (ok === "locked") {
          // the online copy is encrypted and this device can't write to it yet
          const cloud = await pullCloudData(session.user.id);
          if (cloud?.locked) { setEncOn(true); openSyncLock(cloud.envelope, "push"); }
          setSyncStatus("locked");
        } else setSyncStatus("error");   // still marked unsent: it goes out on the next sync
      }
    })();
  }, [profiles, activeId, loaded, session, syncLock]);

  // ---- keeping devices in step -------------------------------------------------------------------------------------
  // Changes are pushed the moment they're made. To see changes made elsewhere the app (1) listens for live updates from the
  // server, (2) checks again whenever it's opened or comes back online, and (3) checks every 25 seconds while it's on screen.
  // "Sync now" does the same check on demand.
  async function syncNow(manual = false) {
    if (!syncConfigured || !session || syncLock || !loaded || syncBusyRef.current) return;
    syncBusyRef.current = true;
    if (manual) setSyncStatus("syncing");
    try {
      const uid = session.user.id;
      if (await remoteChanged(uid)) {
        const firstContact = !hasStamp(uid);
        const cloud = await pullCloudData(uid);
        if (cloud?.locked) { setEncOn(true); openSyncLock(cloud.envelope, firstContact ? "signin" : "push"); setSyncStatus("locked"); return; }
        if (cloud?.encrypted) setEncOn(true);
        const remote = cloud?.data;
        const hasRemote = !!remote?.profiles?.length;
        const local = localSnapshotRef.current;
        if (hasRemote) {
          if (firstContact && local.profiles.length) {
            await adoptCloudData(uid, remote, "signin");                    // first time this device meets the account: ask
          } else if (dirtyRef.current) {
            const useCloud = window.confirm("This account was changed on another device while this device had changes that hadn't synced yet. Press OK to load the other device's changes (this device's unsynced changes are replaced), or Cancel to keep this device's version and overwrite the account.");
            if (useCloud) { skipPushRef.current = true; dirtyRef.current = false; applyCloudProfiles(remote.profiles, remote.activeId, true); }
            else { const ok = await pushCloudData(uid, local); if (ok === true) dirtyRef.current = false; }
          } else {
            skipPushRef.current = true;
            applyCloudProfiles(remote.profiles, remote.activeId, true);     // quietly bring in the other device's changes
          }
        }
        // an empty online copy never wipes this device: the next change here sends this device's data back up
      } else if (dirtyRef.current) {
        const ok = await pushCloudData(uid, localSnapshotRef.current);
        if (ok === true) dirtyRef.current = false;
      }
      setLastSynced(new Date());
      setSyncStatus("synced");
    } catch (e) {
      setSyncStatus("error");
    } finally { syncBusyRef.current = false; }
  }
  useEffect(() => { syncNowRef.current = syncNow; });

  useEffect(() => {
    if (!syncConfigured || !session || !loaded || syncLock) return;
    const uid = session.user.id;
    const go = () => syncNowRef.current(false);
    go();                                                           // right away: catches a new sign-in or an update made while the app was closed
    const unsubscribe = subscribeToRemoteChanges(uid, go);          // live updates (when realtime is enabled on the table)
    const timer = setInterval(() => { if (document.visibilityState === "visible") go(); }, 25000);
    const onVis = () => { if (document.visibilityState === "visible") go(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", go);
    return () => { unsubscribe(); clearInterval(timer); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("online", go); };
  }, [session?.user?.id, loaded, syncLock]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  const active = profiles.find((p) => p.id === activeId) || null;
  useEffect(() => { if (active?.type === "business" && view === "invest") setView("dashboard"); }, [active?.type, view]); // eslint-disable-line react-hooks/exhaustive-deps
  const currencySymbol = (CURRENCIES.find((c) => c.code === (active?.currency || "USD")) || CURRENCIES[0]).symbol;
  function fmt(n) {
    const v = Number(n || 0);
    return `${v < 0 ? "-" : ""}${currencySymbol}${groupNum(Math.abs(v), active?.currency || "USD")}`;
  }
  // The person's country (stored on every profile so it syncs and backs up with everything else)
  const userCountry = SUPPORTED_COUNTRY((profiles.find((p) => p.country) || {}).country || null);
  function saveCountry({ country, currency }) {
    // the country applies to every profile; the currency belongs to the profile that's open
    setProfiles((ps) => ps.map((p) => ({ ...p, country, ...(p.id === activeId && currency ? { currency } : {}) })));
    setShowCountry(false); setCountrySkipped(true);
    showToast(`Country set to ${COUNTRIES[country]?.name}${currency ? ` · ${currency}` : ""}`);
  }
  useEffect(() => {
    if (loaded && profiles.length > 0 && !userCountry && !countrySkipped && view !== "onboarding") setShowCountry(true);
  }, [loaded, profiles.length, userCountry, countrySkipped, view]);

  function createProfile(type) {
    const count = profiles.filter((p) => p.type === type).length;
    const name = count === 0 ? PROFILE_META[type].label : `${PROFILE_META[type].label} ${count + 1}`;
    const p = { id: uid(), type, name, budget: null, expenses: [], members: [], clients: [], customFields: [], categories: defaultCategoriesFor(type), categoryBudgets: {}, recurring: [], goals: [], investments: [], watchlist: [], invoices: [], ...businessDefaults(), currency: COUNTRIES[userCountry]?.currency || "USD", ...(userCountry ? { country: userCountry } : {}) };
    setProfiles((prev) => [...prev, p]);
    setActiveId(p.id);
    setView("dashboard");
    setShowSwitcher(false);
    track(profiles.length === 0 ? "signup" : "profile_created", { type });
  }

  function updateActive(fn) {
    setProfiles((prev) => prev.map((p) => (p.id === activeId ? fn(p) : p)));
  }

  function resetAddForm() {
    setAmount(""); setCategory(""); setNote(""); setDate(todayStr());
    setAssignMember("shared"); setAssignClient("none"); setDeductible(true);
    setCustomValues({});
    setEditingExpenseId(null);
  }

  function openAddSheet() {
    resetAddForm();
    setShowAdd(true);
  }

  function openEditSheet(e) {
    setAmount(String(e.amount));
    setCategory(e.category);
    setNote(e.note || "");
    setDate(e.date);
    setAssignMember(e.member || "shared");
    setAssignClient(e.clientId || "none");
    setDeductible(e.deductible !== false);
    setCustomValues(e.custom || {});
    setEditingExpenseId(e.id);
    setShowAdd(true);
  }

  function addExpense() {
    if (!amount || Number(amount) <= 0 || !category) return;
    const payload = {
      amount: Number(amount),
      category,
      note: note.trim(),
      date,
      member: active?.type === "household" ? assignMember : null,
      clientId: active?.type === "business" ? assignClient : null,
      deductible: active?.type === "business" ? deductible : null,
      custom: { ...customValues },
    };
    if (editingExpenseId) {
      updateActive((p) => ({ ...p, expenses: p.expenses.map((e) => (e.id === editingExpenseId ? { ...e, ...payload } : e)) }));
      showToast("Entry updated");
    } else {
      updateActive((p) => ({ ...p, expenses: [{ id: uid(), ...payload }, ...p.expenses] }));
      showToast("Added to tally");
      track("expense_added", { profileType: active?.type });
    }
    resetAddForm();
    setShowAdd(false);
  }

  function deleteExpense(id) {
    updateActive((p) => ({ ...p, expenses: p.expenses.filter((e) => e.id !== id) }));
  }

  function addMember() {
    if (!memberName.trim()) return;
    const m = { id: uid(), name: memberName.trim(), color: CHIP_COLORS[active.members.length % CHIP_COLORS.length] };
    updateActive((p) => ({ ...p, members: [...p.members, m] }));
    setMemberName("");
  }

  function deleteMember(id) {
    updateActive((p) => ({ ...p, members: p.members.filter((m) => m.id !== id) }));
  }

  function openClientSheet(c) {
    setClientSheet({ id: c?.id || null, name: c?.name || "", email: c?.email || "", phone: c?.phone || "", address: c?.address || "" });
  }

  function saveClient() {
    const f = clientSheet;
    if (!f || !f.name.trim()) return;
    const fields = { name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), address: f.address.trim() };
    if (f.id) updateActive((p) => ({ ...p, clients: p.clients.map((c) => (c.id === f.id ? { ...c, ...fields } : c)) }));
    else updateActive((p) => ({ ...p, clients: [...p.clients, { id: uid(), ...fields, color: CHIP_COLORS[p.clients.length % CHIP_COLORS.length] }] }));
    showToast(f.id ? "Client updated" : "Client added");
    setClientSheet(null);
  }

  function deleteClient(id) {
    updateActive((p) => ({ ...p, clients: p.clients.filter((c) => c.id !== id) }));
  }

  function nextInvoiceNumber() {
    return nextInvoiceNumberFor(active?.invoices || [], active?.business?.invoicePrefix || "INV-");
  }

  function resetInvoiceForm(opts = {}) {
    const b = active?.business || {};
    setEditingInvoiceId(null);
    setInvoiceClientId("");
    setInvoiceNumber(nextInvoiceNumber());
    setInvoiceIssueDate(todayStr());
    setInvoiceDueDate(addDays(todayStr(), b.dueDays ?? 14));
    setInvoiceNotes(b.invoiceNotes || "");
    setInvoiceBillTo("");
    setInvoiceTaxRate(b.taxRate === undefined || b.taxRate === "" || b.taxRate === null ? "" : String(b.taxRate));
    setInvoiceRepeat(opts.repeat || "none");
    setInvoiceHourIds([]);
    setInvoiceItems([{ id: uid(), activity: "", description: "", qty: "1", rate: "" }]);
  }

  function startEditInvoice(inv) {
    setEditingInvoiceId(inv.id);
    setInvoiceClientId(inv.clientId || "");
    setInvoiceNumber(inv.number);
    setInvoiceIssueDate(inv.issueDate);
    setInvoiceDueDate(inv.dueDate);
    setInvoiceNotes(inv.notes || "");
    setInvoiceBillTo(inv.billTo || "");
    setInvoiceTaxRate(inv.taxRate ? String(inv.taxRate) : "");
    setInvoiceRepeat("none");
    setInvoiceHourIds([]);
    setInvoiceItems(inv.items.map((it) => ({ ...it, qty: String(it.qty), rate: String(it.rate) })));
  }

  function pickInvoiceClient(id) {
    setInvoiceClientId(id);
    // remember the bill-to details used last time for this client
    if (id && !invoiceBillTo) {
      const last = (active.invoices || []).find((i) => i.clientId === id && i.billTo);
      const c = active.clients.find((x) => x.id === id);
      const saved = [c?.address, c?.email].filter(Boolean).join("\n");
      if (last) setInvoiceBillTo(last.billTo);
      else if (saved) setInvoiceBillTo(saved);
    }
  }

  function addInvoiceItem() {
    setInvoiceItems((items) => [...items, { id: uid(), activity: "", description: "", qty: "1", rate: "" }]);
  }

  function updateInvoiceItem(id, field, value) {
    setInvoiceItems((items) => items.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  }

  function removeInvoiceItem(id) {
    setInvoiceItems((items) => (items.length > 1 ? items.filter((it) => it.id !== id) : items));
  }

  function invoiceItemsTotal(items) {
    return items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  }

  // Hours logged against this client that haven't been billed yet
  function unbilledHoursFor(clientId) {
    if (!clientId) return [];
    return (active.hours || []).filter((h) => h.clientId === clientId && !h.invoiceId && !invoiceHourIds.includes(h.id));
  }

  function addLoggedHoursToInvoice() {
    const pending = unbilledHoursFor(invoiceClientId);
    if (pending.length === 0) return;
    const byStaff = {};
    pending.forEach((h) => { (byStaff[h.staffId] = byStaff[h.staffId] || []).push(h); });
    const lines = Object.entries(byStaff).map(([staffId, list]) => {
      const st = (active.staff || []).find((x) => x.id === staffId);
      const total = list.reduce((s, h) => s + h.hours, 0);
      const dates = list.map((h) => h.date).sort();
      const range = dates[0] === dates[dates.length - 1] ? dates[0] : `${dates[0]} to ${dates[dates.length - 1]}`;
      return { id: uid(), activity: "", description: `${st?.name || "Staff"} — ${total} hrs (${range})`, qty: String(Math.round(total * 100) / 100), rate: String(st?.billRate || st?.payRate || "") };
    });
    setInvoiceItems((items) => {
      const kept = items.filter((it) => it.description.trim() || (it.activity || "").trim() || it.rate);
      return [...kept, ...lines];
    });
    setInvoiceHourIds((ids) => [...ids, ...pending.map((h) => h.id)]);
    showToast(`Added ${pending.length} logged ${pending.length === 1 ? "shift" : "shifts"}`);
  }

  function saveInvoice() {
    const items = invoiceItems
      .filter((it) => (it.description.trim() || (it.activity || "").trim()) && Number(it.rate) > 0)
      .map((it) => ({ id: it.id, activity: (it.activity || "").trim(), description: it.description.trim(), qty: Number(it.qty) || 1, rate: Number(it.rate) }));
    if (items.length === 0 || !invoiceNumber.trim()) return;
    const taxRate = Number(invoiceTaxRate) || 0;
    const number = invoiceNumber.trim();
    const newId = uid();
    const savedId = editingInvoiceId || newId;
    const base = { clientId: invoiceClientId || null, number, issueDate: invoiceIssueDate, dueDate: invoiceDueDate, notes: invoiceNotes.trim(), billTo: invoiceBillTo.trim(), taxRate, items };
    updateActive((p) => {
      const invoices = editingInvoiceId
        ? p.invoices.map((inv) => (inv.id === editingInvoiceId ? { ...inv, ...base } : inv))
        : [{ id: newId, ...base, status: "draft" }, ...p.invoices];
      const hours = invoiceHourIds.length
        ? (p.hours || []).map((h) => (invoiceHourIds.includes(h.id) ? { ...h, invoiceId: savedId, billedOn: number } : h))
        : p.hours;
      let recurringInvoices = p.recurringInvoices || [];
      if (!editingInvoiceId && invoiceRepeat !== "none") {
        const dueDays = Math.max(0, Math.round((new Date(invoiceDueDate) - new Date(invoiceIssueDate)) / 86400000));
        recurringInvoices = [...recurringInvoices, {
          id: uid(), clientId: base.clientId, items: items.map((it) => ({ ...it })), notes: base.notes, billTo: base.billTo, taxRate,
          frequency: invoiceRepeat, anchorDay: Number(invoiceIssueDate.slice(8, 10)), nextDate: advance(invoiceIssueDate, invoiceRepeat, Number(invoiceIssueDate.slice(8, 10))), dueDays, paused: false, autoSent: false,
        }];
      }
      return { ...p, invoices, hours, recurringInvoices };
    });
    if (!editingInvoiceId && invoiceRepeat !== "none") showToast("Invoice created — the next one is scheduled");
    resetInvoiceForm();
    setView("invoices");
  }

  function setInvoiceStatus(id, status) {
    updateActive((p) => ({
      ...p,
      invoices: p.invoices.map((inv) => {
        if (inv.id !== id) return inv;
        if (status === "paid") {
          // part-paid invoice marked paid: the rest is taken as received today
          const bal = invoiceBalance(inv);
          if (inv.payments?.length && bal > 0) return applyPayment(inv, { id: uid(), date: todayStr(), amount: bal, method: inv.paidMethod || "" });
          return { ...inv, status, paidDate: inv.paidDate || todayStr() };
        }
        // moving away from paid undoes the payment that completed it
        let base = inv;
        if (inv.status === "paid" && inv.payments?.length) base = { ...inv, payments: inv.payments.slice(0, -1) };
        const next = { ...base, status };
        delete next.paidDate;
        return next;
      }),
    }));
  }

  function recordInvoicePayment(id, payment) {
    updateActive((p) => ({ ...p, invoices: p.invoices.map((inv) => {
      if (inv.id !== id) return inv;
      return applyPayment(inv, { id: uid(), ...payment });
    }) }));
    showToast("Payment recorded");
  }

  function removeInvoicePayment(id, paymentId) {
    updateActive((p) => ({ ...p, invoices: p.invoices.map((inv) => (inv.id === id ? removePayment(inv, paymentId) : inv)) }));
    showToast("Payment removed");
  }

  function setInvoicePaidMethod(id, paidMethod) {
    updateActive((p) => ({ ...p, invoices: p.invoices.map((inv) => (inv.id === id ? { ...inv, paidMethod: paidMethod.trim() } : inv)) }));
  }

  function deleteInvoice(id) {
    const gone = (active.invoices || []).find((i) => i.id === id);
    if (gone?.online?.token && remote.available()) remote.unpublish(id).catch(() => {});
    updateActive((p) => ({
      ...p,
      invoices: p.invoices.filter((inv) => inv.id !== id),
      hours: (p.hours || []).map((h) => (h.invoiceId === id ? { ...h, invoiceId: null, billedOn: null } : h)),
    }));
    if (viewingInvoiceId === id) setViewingInvoiceId(null);
  }

  // ---- online invoices, viewed tracking, Stripe, server email (all optional; need a synced account) ----
  async function syncOnline({ quiet = false } = {}) {
    if (!remote.available() || !session) return;
    try {
      const data = await remote.sync();
      setStripeInfo({ connected: !!data.stripe?.connected, chargesEnabled: !!data.stripe?.chargesEnabled });
      const map = new Map((data.invoices || []).map((r) => [r.invoice_local_id, r]));
      const viewed = [], paidNow = [];
      profiles.forEach((p) => (p.invoices || []).forEach((inv) => {
        const r = map.get(inv.id); if (!r) return;
        if (r.view_count > (inv.online?.viewCount || 0) && inv.online?.viewCount !== undefined) viewed.push(inv.number);
        if (r.paid_at && inv.status !== "paid") paidNow.push(inv.number);
      }));
      setProfiles((ps) => ps.map((p) => ({ ...p, invoices: (p.invoices || []).map((inv) => {
        const r = map.get(inv.id); if (!r) return inv;
        const next = { ...inv, online: { ...(inv.online || {}), token: r.token, acceptPayments: r.accept_payments, autoReminders: r.auto_reminders, viewCount: r.view_count, firstViewedAt: r.first_viewed_at, lastViewedAt: r.last_viewed_at, paidAt: r.paid_at, remindersSent: r.reminders_sent } };
        if (r.paid_at && inv.status !== "paid") {
          const bal = invoiceBalance(next);
          const paidNext = bal > 0 ? applyPayment(next, { id: uid(), date: r.paid_at.slice(0, 10), amount: bal, method: "Card (online)" }) : next;
          return { ...paidNext, status: "paid", paidDate: r.paid_at.slice(0, 10), paidVia: "online" };
        }
        return next;
      }) })));
      if (paidNow.length) showToast(`${paidNow.join(", ")} paid online 🎉`);
      else if (viewed.length) showToast(`${viewed.join(", ")} viewed by your client`);
    } catch (e) { if (!quiet) showToast(e.message); }
  }

  async function publishInvoice(inv, client, { acceptPayments, autoReminders }) {
    const b = active.business || {};
    const { token } = await remote.publish({
      invoiceId: inv.id, currency: active.currency || "USD", acceptPayments, autoReminders,
      invoice: { number: inv.number, issueDate: inv.issueDate, dueDate: inv.dueDate, notes: inv.notes, billTo: inv.billTo, taxRate: inv.taxRate, items: inv.items, status: inv.status },
      business: { name: b.name || active.name, address: b.address, email: b.email, phone: b.phone, website: b.website, taxId: b.taxId, taxLabel: b.taxLabel, paymentInfo: b.paymentInfo, logo: b.logo, branding: b.emailBranding !== false },
      client: { name: client?.name, email: client?.email },
    });
    updateActive((p) => ({ ...p, invoices: p.invoices.map((i) => (i.id === inv.id ? { ...i, online: { viewCount: 0, ...(i.online || {}), token, publishedAt: i.online?.publishedAt || new Date().toISOString(), acceptPayments: !!acceptPayments, autoReminders: !!autoReminders } } : i)) }));
    return token;
  }
  async function unpublishInvoice(inv) {
    await remote.unpublish(inv.id);
    updateActive((p) => ({ ...p, invoices: p.invoices.map((i) => (i.id === inv.id ? { ...i, online: null } : i)) }));
  }
  function logReminder(invId, via, tone) {
    updateActive((p) => ({ ...p, invoices: p.invoices.map((i) => (i.id === invId ? { ...i, reminders: [...(i.reminders || []), { date: todayStr(), via, tone }] } : i)) }));
    showToast("Reminder logged");
  }
  const refreshStripe = async () => { const s = await remote.connectStatus(); setStripeInfo({ connected: !!s.connected, chargesEnabled: !!s.chargesEnabled }); };
  const ctx = {
    available: remote.available(), signedIn: !!session, isPaid: accountPlan === "paid" && !!session, config: serverCfg, stripe: stripeInfo,
    publish: publishInvoice, unpublish: unpublishInvoice, logReminder, refreshStripe,
    refreshConfig: () => remote.config().then(setServerCfg),
    send: async (p) => { const r = await remote.send(p); remote.config().then(setServerCfg).catch(() => {}); return r; },   // keeps the daily-usage meter current connectOnboard: () => remote.connectOnboard(),
  };

  useEffect(() => { if (session && remote.available()) remote.config().then(setServerCfg).catch(() => {}); }, [session]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session || !loaded || !remote.available()) return;
    if (["invoices", "business", "bizPayments"].includes(view)) syncOnline({ quiet: true });
  }, [view, session, loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session || !remote.available()) return undefined;
    let last = 0;
    const on = () => { if (Date.now() - last > 60000) { last = Date.now(); syncOnline({ quiet: true }); } };
    window.addEventListener("focus", on);
    return () => window.removeEventListener("focus", on);
  }, [session, profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Used only when "review before printing or emailing" is switched off in Settings
  async function printInvoice(id) {
    const inv = (active.invoices || []).find((i) => i.id === id);
    if (!inv) return;
    const client = (active.clients || []).find((c) => c.id === inv.clientId);
    try {
      await printPdf(async () => {
        const { invoicePdf } = await import("./pdfDocs.js");
        return pdfFile(await invoicePdf(inv, client, active, { branding: active.business?.emailBranding !== false }), safeFilename(`${inv.number}.pdf`));
      });
    } catch (e) { showToast("Couldn't create the PDF"); }
  }

  function emailInvoice(inv) {
    const client = active.clients.find((c) => c.id === inv.clientId);
    if (!client?.email) { setViewingInvoiceId(inv.id); return; } // need an address → open the review screen
    const m = buildInvoiceEmail(inv, client, active, fmt);
    window.location.href = mailtoLink(client.email, m.subject, m.body);
  }

  function buildDraftInvoice() {
    const items = invoiceItems
      .filter((it) => it.description.trim() && Number(it.rate) > 0)
      .map((it) => ({ id: it.id, description: it.description.trim(), qty: Number(it.qty) || 1, rate: Number(it.rate) }));
    const existing = editingInvoiceId ? (active.invoices || []).find((i) => i.id === editingInvoiceId) : null;
    return {
      id: editingInvoiceId || "draft", clientId: invoiceClientId || null, number: invoiceNumber.trim() || "—",
      issueDate: invoiceIssueDate, dueDate: invoiceDueDate, notes: invoiceNotes.trim(), billTo: invoiceBillTo.trim(),
      taxRate: Number(invoiceTaxRate) || 0, items, status: existing?.status || "draft",
    };
  }

  function openDraftReview() {
    const d = buildDraftInvoice();
    if (d.items.length === 0) { showToast("Add a line with a description and rate first"); return; }
    setReviewDraft(d);
  }

  function resetFieldForm() {
    setFieldLabel(""); setFieldType("text"); setFieldOptions(""); setEditingFieldId(null);
  }

  function startEditField(f) {
    setEditingFieldId(f.id);
    setFieldLabel(f.label);
    setFieldType(f.type);
    setFieldOptions((f.options || []).join(", "));
  }

  function saveField() {
    if (!fieldLabel.trim()) return;
    const options = fieldType === "choice" ? fieldOptions.split(",").map((o) => o.trim()).filter(Boolean) : [];
    if (editingFieldId) {
      updateActive((p) => ({
        ...p,
        customFields: p.customFields.map((f) => (f.id === editingFieldId ? { ...f, label: fieldLabel.trim(), type: fieldType, options } : f)),
      }));
    } else {
      const f = { id: uid(), label: fieldLabel.trim(), type: fieldType, options };
      updateActive((p) => ({ ...p, customFields: [...p.customFields, f] }));
    }
    resetFieldForm();
  }

  function deleteField(id) {
    updateActive((p) => ({ ...p, customFields: p.customFields.filter((f) => f.id !== id) }));
    if (editingFieldId === id) resetFieldForm();
  }

  function resetCategoryForm() {
    setCategoryDraft("");
    setEditingCategoryOriginal(null);
  }

  function startEditCategory(name) {
    setEditingCategoryOriginal(name);
    setCategoryDraft(name);
  }

  function saveCategory() {
    const name = categoryDraft.trim();
    if (!name) return;
    const isDuplicate = active.categories.some((c) => c.toLowerCase() === name.toLowerCase() && c !== editingCategoryOriginal);
    if (isDuplicate) { showToast("That category already exists"); return; }
    if (editingCategoryOriginal) {
      updateActive((p) => ({
        ...p,
        categories: p.categories.map((c) => (c === editingCategoryOriginal ? name : c)),
        expenses: p.expenses.map((e) => (e.category === editingCategoryOriginal ? { ...e, category: name } : e)),
      }));
    } else {
      updateActive((p) => ({ ...p, categories: [...p.categories, name] }));
    }
    resetCategoryForm();
  }

  function deleteCategory(name) {
    if (active.categories.length <= 1) { showToast("Keep at least one category"); return; }
    updateActive((p) => ({ ...p, categories: p.categories.filter((c) => c !== name) }));
    if (editingCategoryOriginal === name) resetCategoryForm();
  }

  function saveBudget() {
    const val = Number(budgetDraft);
    updateActive((p) => ({ ...p, budget: val > 0 ? val : null }));
    setEditingBudget(false);
    setBudgetDraft("");
  }

  function setCategoryBudget(cat, value) {
    const num = value === "" ? undefined : Number(value);
    updateActive((p) => {
      const budgets = { ...(p.categoryBudgets || {}) };
      if (num === undefined || isNaN(num) || num <= 0) delete budgets[cat];
      else budgets[cat] = num;
      return { ...p, categoryBudgets: budgets };
    });
  }

  function resetRecurringForm() {
    setRecurringAmount(""); setRecurringCategory(""); setRecurringNote("");
    setRecurringFrequency("monthly"); setRecurringStartDate(todayStr());
    setEditingRecurringId(null);
  }

  function startEditRecurring(r) {
    setEditingRecurringId(r.id);
    setRecurringAmount(String(r.amount));
    setRecurringCategory(r.category);
    setRecurringNote(r.note || "");
    setRecurringFrequency(r.frequency);
    setRecurringStartDate(r.nextDate);
  }

  function saveRecurring() {
    if (!recurringAmount || Number(recurringAmount) <= 0 || !recurringCategory) return;
    if (editingRecurringId) {
      updateActive((p) => ({
        ...p,
        recurring: p.recurring.map((r) => (r.id === editingRecurringId ? { ...r, amount: Number(recurringAmount), category: recurringCategory, note: recurringNote.trim(), frequency: recurringFrequency } : r)),
      }));
    } else {
      const r = { id: uid(), amount: Number(recurringAmount), category: recurringCategory, note: recurringNote.trim(), frequency: recurringFrequency, nextDate: recurringStartDate };
      updateActive((p) => ({ ...p, recurring: [...p.recurring, r] }));
    }
    resetRecurringForm();
  }

  function deleteRecurring(id) {
    updateActive((p) => ({ ...p, recurring: p.recurring.filter((r) => r.id !== id) }));
    if (editingRecurringId === id) resetRecurringForm();
  }

  function addGoal() {
    if (!goalName.trim() || !goalTarget || Number(goalTarget) <= 0) return;
    const g = { id: uid(), name: goalName.trim(), target: Number(goalTarget), saved: 0, targetDate: goalDate || null };
    updateActive((p) => ({ ...p, goals: [...p.goals, g] }));
    setGoalName(""); setGoalTarget(""); setGoalDate("");
  }

  function deleteGoal(id) {
    updateActive((p) => ({ ...p, goals: p.goals.filter((g) => g.id !== id) }));
  }

  function contributeToGoal(id) {
    const val = Number(contributionDrafts[id]);
    if (!val || val <= 0) return;
    updateActive((p) => ({ ...p, goals: p.goals.map((g) => (g.id === id ? { ...g, saved: g.saved + val } : g)) }));
    setContributionDrafts((d) => ({ ...d, [id]: "" }));
  }

  function exportBackup() {
    const data = JSON.stringify({ profiles, activeId, exportedAt: new Date().toISOString() }, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `balance-beyond-backup-${todayStr()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    recordBackup();
    showToast("Backup exported");
  }

  function handleRestoreFile(file) {
    if (!window.confirm("Restore from this backup? This replaces every profile and entry currently on this device.")) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data || !Array.isArray(data.profiles)) throw new Error("bad format");
        const normalized = data.profiles.map((p) => ({ customFields: [], categories: defaultCategoriesFor(p.type), categoryBudgets: {}, recurring: [], goals: [], investments: [], watchlist: [], invoices: [], ...businessDefaults(), currency: "USD", ...p }));
        setProfiles(normalized);
        setActiveId(data.activeId && normalized.find((p) => p.id === data.activeId) ? data.activeId : normalized[0]?.id || null);
        setView(normalized.length ? "dashboard" : "onboarding");
        showToast("Backup restored");
      } catch (err) {
        showToast("Couldn't read that backup file");
      }
    };
    reader.readAsText(file);
  }

  function applyCloudProfiles(rawProfiles, activeIdVal, quiet = false) {
    const normalized = rawProfiles.map((p) => ({ customFields: [], categories: defaultCategoriesFor(p.type), categoryBudgets: {}, recurring: [], goals: [], investments: [], watchlist: [], invoices: [], ...businessDefaults(), currency: "USD", ...p }));
    setProfiles(normalized);
    if (quiet) {
      // a background update: stay on the same screen and on the profile this device has open
      const mine = localSnapshotRef.current.activeId;
      setActiveId(normalized.some((p) => p.id === mine) ? mine : (activeIdVal || normalized[0]?.id || null));
      return;
    }
    skipPushRef.current = true;
    setActiveId(activeIdVal || normalized[0]?.id || null);
    setView(normalized.length ? "dashboard" : "onboarding");
  }

  async function handleSignUp() {
    setAuthError("");
    if (!authEmail || !authPassword || !authPhone) { setAuthError("Fill in all three fields."); return; }
    const weak = passwordProblem(authPassword);
    if (weak) { setAuthError(weak); return; }
    if (!authAgree) { setAuthError("Please tick the box to accept the Terms & Conditions and Privacy Policy."); return; }
    setAuthLoading(true);
    const { data, error } = await signUp(authEmail, authPassword, authPhone, consentRecord());
    setAuthLoading(false);
    if (error) { setAuthError(error.message); return; }
    track("signup_form_submitted");
    if (data?.session) {
      // Email confirmation is off in this Supabase project — session is live immediately.
      setSession(data.session);
      const local = localSnapshotRef.current;
      if (local.profiles.length) await pushCloudData(data.session.user.id, local);
      showToast("Account created — syncing this device");
    } else {
      showToast("Check your email to confirm your account, then log in");
      setAuthMode("login");
    }
    setAuthPassword("");
  }

  async function handleForgotPassword() {
    setAuthError(""); setAuthInfo("");
    if (!authEmail || !/^\S+@\S+\.\S+$/.test(authEmail)) { setAuthError("Enter the email you signed up with."); return; }
    setAuthLoading(true);
    const { error } = await sendPasswordReset(authEmail);
    setAuthLoading(false);
    if (error) { setAuthError(/rate|seconds|too many/i.test(error.message) ? "Please wait a minute before asking again." : error.message); return; }
    // the same message whether or not an account exists, so this can't be used to find out who has one
    setAuthInfo("If an account exists for that email, a reset link is on its way. Open it on this device to choose a new password. Check your junk folder too.");
  }

  async function handleSignIn() {
    setAuthError("");
    if (!authEmail || !authPassword) { setAuthError("Enter your email and password."); return; }
    setAuthLoading(true);
    const { data, error } = await signIn(authEmail, authPassword);
    setAuthLoading(false);
    if (error) { setAuthError(error.message); return; }
    setAuthPassword("");
    const newSession = data.session;
    syncBusyRef.current = true;   // hold the background check back until this sign-in has loaded the account's data
    try {
      setSession(newSession);
      const cloud = await pullCloudData(newSession.user.id);
      if (cloud?.plan) setAccountPlan(cloud.plan);
      if (cloud?.locked) {
        setEncOn(true);
        openSyncLock(cloud.envelope, "signin");
        showToast("Enter your encryption passphrase to load your data");
        return;
      }
      if (cloud?.encrypted) setEncOn(true);
      await adoptCloudData(newSession.user.id, cloud?.data, "signin");
      showToast("Signed in — this device will stay in sync");
    } finally { syncBusyRef.current = false; }
  }

  // Decide what to do when the online copy (already decrypted if it was encrypted) meets this device's data.
  async function adoptCloudData(uid, cloudData, mode) {
    const hasCloud = Boolean(cloudData?.profiles?.length);
    const local = localSnapshotRef.current;
    const hasLocal = local.profiles.length > 0;
    if (hasCloud && hasLocal && mode === "signin") {
      const useCloud = window.confirm(
        "We found saved data both in your account and on this device. Press OK to load your account's data (recommended), or Cancel to keep this device's data and overwrite your account with it."
      );
      if (useCloud) applyCloudProfiles(cloudData.profiles, cloudData.activeId);
      else await pushCloudData(uid, local);
    } else if (hasCloud) {
      applyCloudProfiles(cloudData.profiles, cloudData.activeId);
    } else if (hasLocal) {
      await pushCloudData(uid, local);
    }
  }

  async function handleUnlockSync(passphrase, remember) {
    const uid = session.user.id;
    const data = await unlockEncryption(uid, passphrase, syncLock.envelope, remember);   // throws "doesn't match" if wrong
    const mode = syncLock.mode;
    setSyncLock(null);
    setEncOn(true);
    await adoptCloudData(uid, data, mode);
    showToast("Unlocked. This device is in sync");
  }

  async function handleEnableEncryption(passphrase, remember) {
    await enableEncryption(session.user.id, passphrase, localSnapshotRef.current, remember);
    setEncOn(true);
    setSyncStatus("synced");
    showToast("End-to-end encryption is on");
  }

  // Forgot the passphrase: replace the unreadable online copy with this device's own data (after the confirmation screen).
  async function handleForgotPassphrase() {
    const uid = session.user.id;
    await disableEncryption(uid, localSnapshotRef.current);
    lastPushedJsonRef.current = JSON.stringify(localSnapshotRef.current);
    dirtyRef.current = false;
    setEncOn(false); setSyncLock(null); setShowForgotPass(false);
    setSyncStatus("synced"); setLastSynced(new Date());
    showToast("Online copy replaced with this device's data. You can turn encryption on again with a new passphrase.");
  }

  async function handleDisableEncryption() {
    await disableEncryption(session.user.id, localSnapshotRef.current);
    setEncOn(false);
    showToast("Encryption turned off");
  }

  async function handleSignOut() {
    const uid = session?.user?.id;
    await signOut();
    if (uid) await forgetEncryptionKey(uid);   // signing out forgets the key; signing in again asks for the passphrase
    setSession(null);
    setSyncStatus("idle");
    setEncOn(false);
    setSyncLock(null);
  }

  async function handleDeleteAccount({ eraseDevice }) {
    const uid = session.user.id;
    await deleteAccountOnServer();            // throws with a readable message if it fails; nothing local changes then
    await forgetEncryptionKey(uid);
    try { await signOut(); } catch (e) { /* the account is already gone */ }
    setSession(null); setSyncStatus("idle"); setEncOn(false); setSyncLock(null); setShowDelete(false); setAccountPlan("free");
    if (eraseDevice) { setProfiles([]); setActiveId(null); setView("onboarding"); } else setView("settings");
    setTimeout(() => showToast("Your account has been deleted"), 400);
  }

  const anchorDate = period === "week" ? weekAnchor : period === "month" ? monthAnchor : yearAnchor;
  function setAnchorDate(val) {
    if (period === "week") setWeekAnchor(val);
    else if (period === "month") setMonthAnchor(val);
    else setYearAnchor(val);
  }

  function shiftPeriod(dir) {
    const d = new Date(anchorDate);
    if (period === "week") d.setDate(d.getDate() + dir * 7);
    else if (period === "month") d.setMonth(d.getMonth() + dir);
    else d.setFullYear(d.getFullYear() + dir);
    setAnchorDate(toISODate(d));
    setSelectedDay(null);
  }

  function changePeriod(p) {
    setPeriod(p);
    setSelectedDay(null);
  }

  function jumpToToday() {
    setAnchorDate(todayStr());
    setSelectedDay(null);
  }

  function renameProfile(id, name) {
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }

  function deleteProfile(id) {
    if (!window.confirm("Delete this profile and everything in it? This can't be undone.")) return;
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    if (activeId === id) {
      const remaining = profiles.filter((p) => p.id !== id);
      setActiveId(remaining[0]?.id || null);
      setView(remaining[0] ? "dashboard" : "onboarding");
    }
  }

  function eraseAll() {
    const msg = session
      ? "Erase all profiles and entries? You're signed in to sync, so your online copy is emptied too. This can't be undone. (To remove the account itself, use Delete account.)"
      : "Erase all profiles and entries on this device? This can't be undone.";
    if (!window.confirm(msg)) return;
    setProfiles([]);
    setActiveId(null);
    setView("onboarding");
  }

  function exportCSV(profile) {
    const cols = ["Date", "Category", "Note", "Amount"];
    if (profile.type === "household") cols.push("Member");
    if (profile.type === "business") { cols.push("Client"); cols.push("Deductible"); }
    (profile.customFields || []).forEach((f) => cols.push(f.label));
    const rows = [cols];
    profile.expenses.forEach((e) => {
      const row = [e.date, e.category, e.note || "", e.amount.toFixed(2)];
      if (profile.type === "household") {
        const m = profile.members.find((mm) => mm.id === e.member);
        row.push(e.member === "shared" ? "Shared" : m ? m.name : "");
      }
      if (profile.type === "business") {
        const c = profile.clients.find((cc) => cc.id === e.clientId);
        row.push(e.clientId === "none" ? "" : c ? c.name : "");
        row.push(e.deductible ? "Yes" : "No");
      }
      (profile.customFields || []).forEach((f) => row.push((e.custom && e.custom[f.id]) || ""));
      rows.push(row);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${profile.name.toLowerCase().replace(/\s+/g, "-")}-tally.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast("Exported CSV");
  }

  const monthTotal = active ? active.expenses.filter((e) => isThisMonth(e.date)).reduce((s, e) => s + e.amount, 0) : 0;
  const memberTotals = active?.type === "household"
    ? active.members.map((m) => ({ ...m, total: active.expenses.filter((e) => e.member === m.id && isThisMonth(e.date)).reduce((s, e) => s + e.amount, 0) }))
    : [];
  const sharedTotal = active?.type === "household"
    ? active.expenses.filter((e) => e.member === "shared" && isThisMonth(e.date)).reduce((s, e) => s + e.amount, 0)
    : 0;
  const deductibleTotal = active?.type === "business"
    ? active.expenses.filter((e) => e.deductible && isThisMonth(e.date)).reduce((s, e) => s + e.amount, 0) : 0;
  const nonDeductibleTotal = active?.type === "business"
    ? active.expenses.filter((e) => !e.deductible && isThisMonth(e.date)).reduce((s, e) => s + e.amount, 0) : 0;
  const clientTotals = active?.type === "business"
    ? active.clients.map((c) => ({ ...c, total: active.expenses.filter((e) => e.clientId === c.id && isThisMonth(e.date)).reduce((s, e) => s + e.amount, 0) }))
    : [];

  const monthSpendByCategory = {};
  if (active) {
    active.expenses.filter((e) => isThisMonth(e.date)).forEach((e) => { monthSpendByCategory[e.category] = (monthSpendByCategory[e.category] || 0) + e.amount; });
  }

  const filteredExpenses = active
    ? active.expenses.filter((e) => {
        const q = ledgerSearch.trim().toLowerCase();
        const matchesSearch = !q || e.category.toLowerCase().includes(q) || (e.note || "").toLowerCase().includes(q);
        const matchesCategory = ledgerCategoryFilter === "All" || e.category === ledgerCategoryFilter;
        return matchesSearch && matchesCategory;
      })
    : [];

  const anchorObj = new Date(anchorDate);
  const anchorYear = anchorObj.getFullYear();
  const [weekStart, weekEnd] = getWeekRange(anchorDate);
  const periodExpenses = active
    ? active.expenses.filter((e) => (period === "week" ? isInRange(e.date, weekStart, weekEnd) : isInMonth(e.date, anchorObj.getFullYear(), anchorObj.getMonth())))
    : [];
  const yearExpenses = active ? active.expenses.filter((e) => new Date(e.date).getFullYear() === anchorYear) : [];
  const periodBreakdown = categoryBreakdown(period === "year" ? yearExpenses : periodExpenses);
  const periodMemberBreakdown = active?.type === "household" ? memberBreakdown(period === "year" ? yearExpenses : periodExpenses, active.members) : [];
  const periodLabel = period === "week"
    ? `${formatShort(weekStart)} – ${formatShort(weekEnd)}`
    : period === "month"
    ? anchorObj.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : String(anchorObj.getFullYear());

  const realNow = new Date();
  const [realWeekStart] = getWeekRange(realNow);
  const atCurrentPeriod = period === "week"
    ? weekStart.getTime() === realWeekStart.getTime()
    : period === "month"
    ? anchorObj.getFullYear() === realNow.getFullYear() && anchorObj.getMonth() === realNow.getMonth()
    : anchorObj.getFullYear() === realNow.getFullYear();

  const monthTotalsArr = Array.from({ length: 12 }, (_, m) =>
    active ? active.expenses.filter((e) => { const d = new Date(e.date); return d.getFullYear() === anchorYear && d.getMonth() === m; }).reduce((s, e) => s + e.amount, 0) : 0
  );
  const activeMonthTotals = monthTotalsArr.filter((t) => t > 0);
  const minMonthTotal = activeMonthTotals.length ? Math.min(...activeMonthTotals) : 0;
  const maxMonthTotal = activeMonthTotals.length ? Math.max(...activeMonthTotals) : 0;
  const yearTotal = monthTotalsArr.reduce((s, v) => s + v, 0);

  const periodTotal = period === "year" ? yearTotal : periodExpenses.reduce((s, e) => s + e.amount, 0);

  const isRealCurrentYear = anchorYear === realNow.getFullYear();
  const realDoy = dayOfYear(realNow);
  const realIsLeap = (realNow.getFullYear() % 4 === 0 && realNow.getFullYear() % 100 !== 0) || realNow.getFullYear() % 400 === 0;
  const realDaysInYear = realIsLeap ? 366 : 365;
  const projectedYearly = isRealCurrentYear && realDoy > 0 ? (yearTotal / realDoy) * realDaysInYear : null;

  const daysInMonthCount = new Date(anchorObj.getFullYear(), anchorObj.getMonth() + 1, 0).getDate();
  const firstOfMonth = new Date(anchorObj.getFullYear(), anchorObj.getMonth(), 1);
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
  const dayTotalsMap = {};
  if (active) {
    active.expenses.forEach((e) => {
      if (isInMonth(e.date, anchorObj.getFullYear(), anchorObj.getMonth())) dayTotalsMap[e.date] = (dayTotalsMap[e.date] || 0) + e.amount;
    });
  }
  const maxDayTotal = Math.max(0, ...Object.values(dayTotalsMap));
  const calendarDays = Array.from({ length: daysInMonthCount }, (_, i) => {
    const dNum = i + 1;
    const dStr = `${anchorObj.getFullYear()}-${String(anchorObj.getMonth() + 1).padStart(2, "0")}-${String(dNum).padStart(2, "0")}`;
    return { day: dNum, dateStr: dStr, total: dayTotalsMap[dStr] || 0 };
  });
  const selectedDayExpenses = selectedDay && active ? active.expenses.filter((e) => e.date === selectedDay) : [];
  const selectedDayTotal = selectedDayExpenses.reduce((s, e) => s + e.amount, 0);

  const tipsForProfile = TIPS.filter((t) => !t.biz || active?.type === "business");
  // App lock: nothing from the app is rendered until the PIN (or fingerprint / face) is accepted.
  const showTabs = active && ["dashboard", "insights", "invest", "members", "business", "settings", "fields", "tips", "privacy", "categories", "budgets", "recurring", "goals", "import", "account", "install", "guide", "invoices", "addInvoice", "bizPL", "payslips", "taxHelp", "bizPayments", "mileage", "hours", "bizDetails", "recurringInvoices", "clients"].includes(view);

  if (appLocked && lockOn) return <LockScreen onUnlock={() => setAppLocked(false)} />;

  return (
    <div className="vellum-app min-h-screen w-full flex justify-center p-0 sm:p-6 lg:p-0" style={{ background: "var(--page)" }}>
      <style>{APP_CSS}</style>

      <div className={`app-shell relative w-full bg-ink text-ivory ${showTabs ? "max-w-sm md:max-w-2xl lg:max-w-6xl has-nav" : "max-w-sm md:max-w-md"}`} style={{ minHeight: "100vh" }}>
        {toast && (
          <div className="fixed left-1/2 z-50 v-mono text-xs px-4 py-2 rounded-full bg-gold-grad text-ink font-semibold shadow-lg" style={{ bottom: "88px", transform: "translateX(-50%)" }}>
            {toast}
          </div>
        )}

        {view === "onboarding" && (
          <div className="flex flex-col items-center text-center px-6 pt-16 pb-10">
            <img src="/logo-mark.png" alt="Balance & Beyond" className="w-20 h-20 rounded-2xl mb-6" style={{ boxShadow: "0 6px 24px rgba(217,178,107,0.18)" }} />
            <h1 className="v-display text-3xl font-semibold mb-3 text-center" style={{ letterSpacing: "0.01em" }}>Balance & Beyond</h1>
            <div className="gold-rule mb-3" />
            <p className="text-ivory-dim text-sm mb-10">Track your money. Keep your privacy.</p>

            <div className="w-full flex flex-col gap-6 mb-12 text-left">
              <div className="flex gap-3 items-start">
                <WifiOff size={18} className="text-brass mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Works offline</div>
                  <div className="text-ivory-dim text-xs mt-0.5">No bank login needed. Works on a plane, in a tunnel, anywhere.</div>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <Lock size={18} className="text-brass mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Private by default</div>
                  <div className="text-ivory-dim text-xs mt-0.5">Entries stay on this device unless you export them or choose to sync an account.</div>
                </div>
              </div>
            </div>

            <button onClick={() => setView("newProfile")} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm">
              Get started
            </button>
          </div>
        )}

        {view === "newProfile" && (
          <div className="px-6 pt-8 pb-10">
            <div className="flex items-center gap-3 mb-8">
              {profiles.length > 0 && (
                <button onClick={() => setView("dashboard")} className="p-1 -ml-1">
                  <ArrowLeft size={18} className="text-ivory-dim" />
                </button>
              )}
              <h2 className="v-display text-xl font-semibold">What are you tracking?</h2>
            </div>

            <div className="flex flex-col gap-3">
              {Object.entries(PROFILE_META).map(([type, meta]) => {
                const Icon = meta.icon;
                return (
                  <button key={type} onClick={() => createProfile(type)} className="bg-card border border-hair rounded-2xl p-4 text-left flex gap-3 items-start">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.accent}22` }}>
                      <Icon size={17} style={{ color: meta.accent }} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{meta.label}</div>
                      <div className="text-ivory-dim text-xs mt-0.5">{meta.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {view === "dashboard" && active && (
          <div className="pb-28">
            <div className="sticky top-0 z-20 bg-ink border-b border-hair px-5 py-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
              <div className="relative min-w-0">
                <button onClick={() => setShowSwitcher((s) => !s)} className="flex items-center gap-2 bg-card2 rounded-full pl-1 pr-3 py-1">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${PROFILE_META[active.type].accent}33` }}>
                    {(() => { const Icon = PROFILE_META[active.type].icon; return <Icon size={12} style={{ color: PROFILE_META[active.type].accent }} />; })()}
                  </div>
                  <span className="text-xs font-medium truncate">{active.name}</span>
                  <ChevronDown size={14} className="text-ivory-dim" />
                </button>
                {showSwitcher && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowSwitcher(false)} />
                    <div className="absolute top-full left-0 mt-2 w-60 bg-card2 border border-hair rounded-xl shadow-xl z-20 overflow-hidden">
                      {profiles.map((p) => {
                        const Icon = PROFILE_META[p.type].icon;
                        return (
                          <button key={p.id} onClick={() => { setActiveId(p.id); setShowSwitcher(false); setView("dashboard"); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-left" style={{ background: p.id === activeId ? "var(--brass-dim)" : "transparent" }}>
                            <Icon size={14} style={{ color: PROFILE_META[p.type].accent }} />
                            <span className="text-xs flex-1">{p.name}</span>
                            {p.id === activeId && <Check size={13} className="text-brass" />}
                          </button>
                        );
                      })}
                      <button onClick={() => setView("newProfile")} className="w-full flex items-center gap-2 px-3 py-2.5 text-left border-t border-hair">
                        <Plus size={14} className="text-brass" />
                        <span className="text-xs text-brass font-medium">New profile</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-card2 rounded-full px-2.5 py-1 shrink-0">
                  <Lock size={11} className="text-sage" />
                  <span className="text-xs text-sage font-medium whitespace-nowrap">Private · Offline</span>
                </div>
                {session && (
                  <button onClick={() => syncNow(true)} title="Sync now" aria-label="Sync now" className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--card2)" }}>
                    <RefreshCw size={13} className={`text-ivory-dim ${syncStatus === "syncing" ? "animate-spin" : ""}`} />
                  </button>
                )}
                <button
                  onClick={session ? handleSignOut : () => setView("account")}
                  title={session ? "Log out" : "Log in"}
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "var(--card2)" }}
                >
                  {session ? <LogOut size={13} className="text-ivory-dim" /> : <UserCircle size={14} className="text-ivory-dim" />}
                </button>
              </div>
            </div>

            <div className="wide-flow">
            <div className="px-5 pt-6">
              <div className="ledger-bg rounded-2xl p-5 card-hero">
                <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Spent this month</div>
                <div className="v-mono text-4xl font-medium">{fmt(monthTotal)}</div>

                {active.type !== "business" && (
                  <div className="mt-4">
                    {active.budget ? (
                      <>
                        <div className="w-full h-1.5 rounded-full bg-card2 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, (monthTotal / active.budget) * 100)}%`, background: monthTotal > active.budget ? "var(--rust)" : "var(--brass)" }} />
                        </div>
                        <div className="text-xs text-ivory-dim mt-1.5">
                          {monthTotal > active.budget
                            ? <span className="text-rust">{fmt(monthTotal - active.budget)} over your {fmt(active.budget)} budget</span>
                            : <span>{fmt(active.budget - monthTotal)} left of {fmt(active.budget)}</span>}
                        </div>
                      </>
                    ) : editingBudget ? (
                      <div className="flex gap-2 items-center mt-1">
                        <input value={budgetDraft} onChange={(e) => setBudgetDraft(e.target.value)} type="number" placeholder="0.00" className="v-mono text-xs rounded-lg px-2 py-1.5 w-24" />
                        <button onClick={saveBudget} className="text-xs text-brass font-medium">Save</button>
                        <button onClick={() => setEditingBudget(false)} className="text-xs text-ivory-dim">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setEditingBudget(true)} className="text-xs text-brass font-medium mt-1">Set a monthly budget</button>
                    )}
                  </div>
                )}

                {active.type === "business" && (
                  <div className="flex gap-4 mt-4">
                    <div>
                      <div className="text-xs text-ivory-dim">Deductible</div>
                      <div className="v-mono text-sm text-sage font-medium">{fmt(deductibleTotal)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-ivory-dim">Not deductible</div>
                      <div className="v-mono text-sm text-ivory-dim font-medium">{fmt(nonDeductibleTotal)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {active.type === "household" && active.members.length > 0 && (
              <div className="px-5 mt-4 flex gap-3 overflow-x-auto">
                {memberTotals.map((m) => (
                  <div key={m.id} className="flex flex-col items-center shrink-0">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: m.color, color: readableOn(m.color) }}>
                      {m.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="text-xs text-ivory-dim mt-1">{m.name}</div>
                    <div className="v-mono text-xs">{fmt(m.total)}</div>
                  </div>
                ))}
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-card2 text-xs text-ivory-dim">∗</div>
                  <div className="text-xs text-ivory-dim mt-1">Shared</div>
                  <div className="v-mono text-xs">{fmt(sharedTotal)}</div>
                </div>
              </div>
            )}

            {active.type === "business" && active.clients.length > 0 && (
              <div className="px-5 mt-4 flex gap-3 overflow-x-auto">
                {clientTotals.map((c) => (
                  <div key={c.id} className="flex flex-col items-center shrink-0">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: c.color, color: readableOn(c.color) }}>
                      {c.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="text-xs text-ivory-dim mt-1">{c.name}</div>
                    <div className="v-mono text-xs">{fmt(c.total)}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="px-5 mt-6">
              <button onClick={openAddSheet} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-1.5">
                <Plus size={15} /> Add expense
              </button>
            </div>

            <div className="px-5 mt-3">
              <div className="grid grid-cols-3 gap-2">
                {active.type !== "business" && (
                  <button onClick={() => setView("budgets")} className="flex flex-col items-center gap-1.5 bg-card2 rounded-xl py-3">
                    <Wallet size={16} className="text-brass" />
                    <span className="text-xs text-ivory-dim">Budgets</span>
                  </button>
                )}
                <button onClick={() => setView("recurring")} className="flex flex-col items-center gap-1.5 bg-card2 rounded-xl py-3">
                  <Repeat size={16} className="text-brass" />
                  <span className="text-xs text-ivory-dim">Recurring</span>
                </button>
                {active.type === "business" ? (
                  <button onClick={() => setView("invoices")} className="flex flex-col items-center gap-1.5 bg-card2 rounded-xl py-3">
                    <FileText size={16} className="text-brass" />
                    <span className="text-xs text-ivory-dim">Invoices</span>
                  </button>
                ) : (
                  <button onClick={() => setView("goals")} className="flex flex-col items-center gap-1.5 bg-card2 rounded-xl py-3">
                    <Target size={16} className="text-brass" />
                    <span className="text-xs text-ivory-dim">Goals</span>
                  </button>
                )}
                {active.type === "business" && (
                  <button onClick={() => setView("bizPL")} className="flex flex-col items-center gap-1.5 bg-card2 rounded-xl py-3">
                    <TrendingUp size={16} className="text-brass" />
                    <span className="text-xs text-ivory-dim">Profit/Loss</span>
                  </button>
                )}
              </div>
            </div>

            {!isStandalone && !installBannerDismissed && (installPromptEvent || isIOSDevice) && (
              <div className="px-5 mt-5">
                <div className="bg-card2 rounded-2xl p-4 flex gap-3 items-start relative">
                  <button onClick={dismissInstallBanner} className="absolute top-2 right-2 opacity-40"><X size={13} /></button>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--brass-dim)" }}>
                    <Smartphone size={14} className="text-brass" />
                  </div>
                  <div className="pr-4">
                    {installPromptEvent ? (
                      <>
                        <div className="text-xs font-semibold">Install this app</div>
                        <div className="text-xs text-ivory-dim mt-1 leading-relaxed">Adds it to your home screen and lets it work offline, like a regular app.</div>
                        <button onClick={triggerInstall} className="text-xs text-brass font-medium mt-2">Install now</button>
                      </>
                    ) : (
                      <>
                        <div className="text-xs font-semibold">Add to your Home Screen</div>
                        <div className="text-xs text-ivory-dim mt-1 leading-relaxed">Tap the Share icon <Share size={11} className="inline" style={{ verticalAlign: "-1px" }} /> below, then "Add to Home Screen."</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!tipDismissed && (
              <div className="px-5 mt-5">
                <div className="bg-card2 rounded-2xl p-4 flex gap-3 items-start relative">
                  <button onClick={() => setTipDismissed(true)} className="absolute top-2 right-2 opacity-40"><X size={13} /></button>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--brass-dim)" }}>
                    <Lightbulb size={14} className="text-brass" />
                  </div>
                  <div className="pr-4">
                    <div className="text-xs font-semibold">{tipsForProfile[Math.floor(tipSeed * tipsForProfile.length)].title}</div>
                    <div className="text-xs text-ivory-dim mt-1 leading-relaxed">{tipsForProfile[Math.floor(tipSeed * tipsForProfile.length)].body}</div>
                    <button onClick={() => setView("tips")} className="text-xs text-brass font-medium mt-2">See all tips</button>
                  </div>
                </div>
              </div>
            )}

            <div className="px-5 mt-7">
              <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Tally · {filteredExpenses.length} {filteredExpenses.length === 1 ? "entry" : "entries"}</div>
              {active.expenses.length > 0 && (
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 flex items-center gap-1.5 bg-card2 rounded-lg px-2.5">
                    <Search size={13} className="text-ivory-dim shrink-0" />
                    <input value={ledgerSearch} onChange={(e) => setLedgerSearch(e.target.value)} placeholder="Search notes, category" className="w-full bg-transparent border-none text-sm py-2" style={{ background: "transparent", border: "none" }} />
                  </div>
                  <select value={ledgerCategoryFilter} onChange={(e) => setLedgerCategoryFilter(e.target.value)} className="rounded-lg px-2 text-xs" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }}>
                    <option value="All">All</option>
                    {active.categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}
              {active.expenses.length === 0 ? (
                <div className="text-center py-10 text-ivory-dim text-sm">
                  No entries yet.<br />Add your first expense above.
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="text-center py-10 text-ivory-dim text-sm">No entries match that search.</div>
              ) : (
                <div>
                  {filteredExpenses.map((e) => {
                    const member = active.type === "household" ? active.members.find((m) => m.id === e.member) : null;
                    const client = active.type === "business" ? active.clients.find((c) => c.id === e.clientId) : null;
                    return (
                      <div key={e.id} className="ledger-row flex items-center justify-between py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: categoryColor(e.category) }} />
                          <div className="min-w-0">
                            <div className="text-sm truncate">{e.category}</div>
                            <div className="text-xs text-ivory-dim flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              {e.note && <span className="truncate" style={{ maxWidth: "160px" }}>{e.note}</span>}
                              {active.type === "household" && (
                                <span className="whitespace-nowrap">· {e.member === "shared" ? "Shared" : member?.name || "Unknown"}</span>
                              )}
                              {active.type === "business" && (
                                <span className="whitespace-nowrap">· {e.deductible ? "Deductible" : "Not deductible"}{client ? ` · ${client.name}` : ""}</span>
                              )}
                              {(active.customFields || []).map((f) => {
                                const v = e.custom && e.custom[f.id];
                                if (!v && v !== 0) return null;
                                return <span key={f.id} className="whitespace-nowrap">· {f.label}: {String(v)}</span>;
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-right">
                            <div className="v-mono text-sm">{fmt(e.amount)}</div>
                            <div className="text-xs text-ivory-dim">{e.date.slice(5)}</div>
                          </div>
                          <button onClick={() => openEditSheet(e)} className="opacity-50">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => deleteExpense(e.id)} className="opacity-40">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {view === "insights" && active && (
          <div className="px-5 pt-8 pb-28 wide-cols">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Chronicle</h2>
            </div>

            <div className="flex gap-2 mb-5">
              <button onClick={() => changePeriod("week")} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: period === "week" ? "var(--brass)" : "var(--card)", color: period === "week" ? "var(--ink)" : "var(--ivory-dim)" }}>Week</button>
              <button onClick={() => changePeriod("month")} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: period === "month" ? "var(--brass)" : "var(--card)", color: period === "month" ? "var(--ink)" : "var(--ivory-dim)" }}>Month</button>
              <button onClick={() => changePeriod("year")} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: period === "year" ? "var(--brass)" : "var(--card)", color: period === "year" ? "var(--ink)" : "var(--ivory-dim)" }}>Year</button>
            </div>

            <div className="ledger-bg rounded-2xl p-5 card-hero mb-2">
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => shiftPeriod(-1)} className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--chip)" }}>
                  <ChevronLeft size={14} className="text-ivory-dim" />
                </button>
                <div className="text-xs uppercase tracking-wide text-ivory-dim text-center">{period === "year" ? `${periodLabel} total` : periodLabel}</div>
                <button onClick={() => shiftPeriod(1)} disabled={atCurrentPeriod} className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 disabled:opacity-25" style={{ background: "var(--chip)" }}>
                  <ChevronRight size={14} className="text-ivory-dim" />
                </button>
              </div>
              <div className="v-mono text-3xl font-medium text-center">{fmt(periodTotal)}</div>
            </div>
            {!atCurrentPeriod && (
              <div className="text-center mb-4">
                <button onClick={jumpToToday} className="text-xs text-brass font-medium">Jump to today</button>
              </div>
            )}
            {atCurrentPeriod && <div className="mb-4" />}

            {period === "month" && (
              <div className="mb-8">
                <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">Daily spend</div>
                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                  {WEEKDAY_LABELS.map((w, i) => (
                    <div key={i} className="text-center text-xs text-ivory-dim">{w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {Array.from({ length: leadingBlanks }).map((_, i) => <div key={`b${i}`} />)}
                  {calendarDays.map((d) => {
                    const intensity = maxDayTotal > 0 ? d.total / maxDayTotal : 0;
                    const isSelected = selectedDay === d.dateStr;
                    const isToday = d.dateStr === todayStr();
                    return (
                      <button
                        key={d.dateStr}
                        onClick={() => setSelectedDay(isSelected ? null : d.dateStr)}
                        className="aspect-square rounded-lg flex items-center justify-center text-xs"
                        style={{
                          background: d.total > 0 ? `rgba(217,178,107,${(0.15 + intensity * 0.65).toFixed(2)})` : "var(--card)",
                          color: d.total > 0 && intensity > 0.5 ? "var(--on-brass)" : "var(--ivory-dim)",
                          boxShadow: isSelected ? "0 0 0 1.5px var(--brass)" : isToday ? "0 0 0 1px var(--ring)" : "none",
                        }}
                      >
                        {d.day}
                      </button>
                    );
                  })}
                </div>
                {selectedDay && (
                  <div className="bg-card2 rounded-xl p-3 mt-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium">{new Date(selectedDay).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</span>
                      <span className="v-mono text-xs text-brass">{fmt(selectedDayTotal)}</span>
                    </div>
                    {selectedDayExpenses.length === 0 ? (
                      <div className="text-xs text-ivory-dim">No entries this day.</div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {selectedDayExpenses.map((e) => (
                          <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-ivory-dim min-w-0 break-words">{e.category}{e.note ? ` · ${e.note}` : ""}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="v-mono">{fmt(e.amount)}</span>
                              <button onClick={() => openEditSheet(e)} className="opacity-60 p-0.5" aria-label="Edit entry"><Pencil size={12} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {period === "year" && (
              <div className="mb-8">
                <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">By month</div>
                <div className="grid grid-cols-4 gap-2">
                  {MONTH_LABELS.map((label, i) => {
                    const total = monthTotalsArr[i];
                    const color = total > 0 ? heatColor(total, minMonthTotal, maxMonthTotal) : null;
                    return (
                      <div key={label} className="rounded-xl p-2.5 flex flex-col items-center gap-1" style={{ background: color || "var(--card)" }}>
                        <span className="text-xs font-medium" style={{ color: color ? "#F8F3E8" : "var(--ivory-dim)" }}>{label}</span>
                        <span className="v-mono text-xs" style={{ color: color ? "#F8F3E8" : "var(--ivory-dim)" }}>{total > 0 ? groupNum(total, active?.currency || "USD") : "—"}</span>
                      </div>
                    );
                  })}
                </div>
                {activeMonthTotals.length > 1 && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-ivory-dim">Lowest</span>
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: "linear-gradient(90deg, #3F8368, #B5445F)" }} />
                    <span className="text-xs text-ivory-dim">Highest</span>
                  </div>
                )}
              </div>
            )}

            <div className="mb-8">
              <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">By category</div>
              {periodBreakdown.length === 0 ? (
                <div className="text-center py-8 text-ivory-dim text-sm">No entries in this period.</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {periodBreakdown.map((b) => {
                    const isOpen = chronCat === b.category;
                    const entries = isOpen
                      ? (period === "year" ? yearExpenses : periodExpenses).filter((e) => e.category === b.category).slice().sort((x, y) => (y.date > x.date ? 1 : y.date < x.date ? -1 : 0))
                      : [];
                    return (
                      <div key={b.category}>
                        <button onClick={() => setChronCat(isOpen ? null : b.category)} className="w-full text-left" aria-expanded={isOpen}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm flex items-center gap-1">{b.category}{isOpen ? <ChevronUp size={13} className="text-ivory-dim" /> : <ChevronDown size={13} className="text-ivory-dim" />}</span>
                            <div className="flex items-center gap-2">
                              <span className="v-mono text-xs text-ivory-dim">{fmt(b.amount)}</span>
                              <span className="v-mono text-xs text-brass w-10 text-right">{b.pct.toFixed(0)}%</span>
                            </div>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-card2 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: categoryColor(b.category) }} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="bg-card2 rounded-xl px-3 py-1 mt-2">
                            {entries.map((e) => (
                              <div key={e.id} className="flex items-center gap-2 py-2 border-b border-hair last:border-0">
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs break-words">{e.note || e.category}</div>
                                  <div className="text-xs text-ivory-dim">{e.date}</div>
                                </div>
                                <span className="v-mono text-xs shrink-0">{fmt(e.amount)}</span>
                                <button onClick={() => openEditSheet(e)} className="opacity-60 p-0.5 shrink-0" aria-label="Edit entry"><Pencil size={13} /></button>
                                <button onClick={() => deleteExpense(e.id)} className="opacity-40 p-0.5 shrink-0" aria-label="Delete entry"><Trash2 size={13} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {active.type === "household" && (
              <div className="mb-8">
                <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">By member</div>
                {periodMemberBreakdown.length === 0 ? (
                  <div className="text-center py-8 text-ivory-dim text-sm">No entries in this period.</div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {periodMemberBreakdown.map((b) => (
                      <div key={b.key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{b.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="v-mono text-xs text-ivory-dim">{fmt(b.amount)}</span>
                            <span className="v-mono text-xs text-brass w-10 text-right">{b.pct.toFixed(0)}%</span>
                          </div>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-card2 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: b.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {period === "year" && projectedYearly !== null && (
              <div className="card-hero rounded-2xl p-5">
                <div className="text-xs uppercase tracking-wide text-ivory-dim mb-1">On track for {anchorYear}</div>
                <div className="v-mono text-2xl font-medium text-brass mb-2">{fmt(projectedYearly)}</div>
                <div className="text-xs text-ivory-dim leading-relaxed">Projected from your average spend since Jan 1 — the estimate settles in as the year goes on.</div>
              </div>
            )}
          </div>
        )}

        {view === "invest" && active && (
          <InvestPage key={active.id} profile={active} update={updateActive} country={userCountry} fmt={fmt} toast={showToast} onBack={() => setView("dashboard")} />
        )}

        {view === "members" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Household members</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Assign an expense to a member when you add it, or leave it shared.</p>
            <div className="flex flex-col gap-2 mb-6">
              {active.members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 bg-card rounded-xl px-3 py-2.5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: m.color, color: readableOn(m.color) }}>
                    {m.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 text-sm">{m.name}</div>
                  <div className="v-mono text-xs text-ivory-dim">{fmt(memberTotals.find((mt) => mt.id === m.id)?.total || 0)}</div>
                  <button onClick={() => deleteMember(m.id)} className="opacity-40"><Trash2 size={14} /></button>
                </div>
              ))}
              {active.members.length === 0 && <div className="text-ivory-dim text-sm text-center py-6">No members yet.</div>}
            </div>
            <div className="flex gap-2">
              <input value={memberName} onChange={(e) => setMemberName(e.target.value)} placeholder="Member name" className="flex-1 rounded-lg px-3 py-2 text-sm" />
              <button onClick={addMember} className="bg-brass text-ink font-semibold rounded-lg px-4 text-sm">Add</button>
            </div>
          </div>
        )}

        {view === "business" && active && (
          <div className="px-5 pt-8 pb-28 wide-cols">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Business</h2>
            </div>

            <BusinessTiles active={active} fmt={fmt} setView={setView} />

            <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Tax deductions</div>
            <div className="bg-card rounded-2xl p-4 mb-6 flex items-center justify-between">
              <div>
                <div className="text-xs text-ivory-dim">Deductible this month</div>
                <div className="v-mono text-lg text-sage font-medium">{fmt(deductibleTotal)}</div>
              </div>
              <button onClick={() => exportCSV(active)} className="flex items-center gap-1.5 bg-card2 rounded-full px-3 py-1.5 text-xs font-medium">
                <Download size={13} /> Export for taxes
              </button>
            </div>
          </div>
        )}

        {view === "clients" && active && (
          <div className="px-5 pt-8 pb-28 wide-cols">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setView("business")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Clients</h2>
            </div>

            <button onClick={() => openClientSheet(null)} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-1.5 mb-6">
              <Plus size={15} /> Add client
            </button>

            {active.clients.length === 0 ? (
              <div className="text-center py-10 text-ivory-dim text-sm">No clients yet.<br />Add one to bill them on an invoice.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {active.clients.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 bg-card rounded-xl p-3.5">
                    <button onClick={() => openClientSheet(c)} className="flex items-center gap-3 flex-1 min-w-0 text-left" aria-label={`Edit ${c.name}`}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0" style={{ background: c.color, color: readableOn(c.color) }}>
                        {c.name.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name}</div>
                        {(c.email || c.phone) && <div className="text-xs text-ivory-dim truncate">{[c.email, c.phone].filter(Boolean).join(" · ")}</div>}
                      </div>
                    </button>
                    <div className="v-mono text-xs text-ivory-dim shrink-0">{fmt(clientTotals.find((ct) => ct.id === c.id)?.total || 0)}</div>
                    <button onClick={() => openClientSheet(c)} className="opacity-60 shrink-0" aria-label="Edit client"><Pencil size={14} /></button>
                    <button onClick={() => deleteClient(c.id)} className="opacity-40 shrink-0" aria-label="Delete client"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "invoices" && active && (
          <div className="px-5 pt-8 pb-28 wide-cols">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Invoices</h2>
            </div>

            <button onClick={() => { resetInvoiceForm(); setView("addInvoice"); }} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-1.5 mb-6">
              <Plus size={15} /> New invoice
            </button>

            {(active.invoices || []).length === 0 ? (
              <div className="text-center py-10 text-ivory-dim text-sm">No invoices yet.<br />Create one to bill a client.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {(active.invoices || []).map((inv) => {
                  const client = active.clients.find((c) => c.id === inv.clientId);
                  const total = invoiceTotals(inv).total;
                  const partial = isPartiallyPaid(inv);
                  const owing = invoiceBalance(inv);
                  const shownStatus = effectiveStatus(inv);
                  const lateDays = shownStatus === "overdue" ? daysOverdue(inv) : 0;
                  const statusColor = shownStatus === "paid" ? "var(--sage)" : shownStatus === "overdue" ? "var(--rust)" : shownStatus === "sent" ? "var(--sent)" : "var(--ivory-dim)";
                  return (
                    <div key={inv.id} className="bg-card rounded-xl p-3.5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{inv.number} {client ? `· ${client.name}` : ""}{inv.fromRecurring ? " ↻" : ""}</div>
                          <div className="text-xs text-ivory-dim">Due {inv.dueDate}{inv.status === "paid" && inv.paidDate ? ` · paid ${inv.paidDate}${inv.paidVia === "online" ? " online" : ""}` : partial ? ` · ${fmt(invoicePaidAmount(inv))} received` : ""}</div>
                          {(inv.online?.token || inv.reminders?.length) && (
                            <div className="text-xs mt-0.5" style={{ color: "var(--brass)" }}>
                              {inv.online?.token && !inv.online.paidAt ? (inv.online.viewCount > 0 ? `👁 Viewed ${inv.online.viewCount}×` : "Link not opened yet") : ""}
                              {inv.reminders?.length ? `${inv.online?.token && !inv.online.paidAt ? " · " : ""}${inv.reminders.length} reminder${inv.reminders.length === 1 ? "" : "s"} sent` : ""}
                            </div>)}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="v-mono text-sm">{fmt(total)}</div>
                          <div className="text-xs font-medium" style={{ color: statusColor }}>{partial ? `${shownStatus === "overdue" ? "Overdue · " : "Partial · "}${fmt(owing)} due` : shownStatus[0].toUpperCase() + shownStatus.slice(1)}{lateDays ? ` · ${lateDays}d` : ""}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {prefs.reviewInvoices ? (
                          <button onClick={() => setViewingInvoiceId(inv.id)} className="flex-1 flex items-center justify-center gap-1 bg-card2 rounded-lg py-1.5 text-xs font-medium"><Printer size={12} /> Review &amp; send</button>
                        ) : (
                          <>
                            <button onClick={() => printInvoice(inv.id)} className="flex-1 flex items-center justify-center gap-1 bg-card2 rounded-lg py-1.5 text-xs font-medium"><Printer size={12} /> Print</button>
                            <button onClick={() => emailInvoice(inv)} className="flex-1 flex items-center justify-center gap-1 bg-card2 rounded-lg py-1.5 text-xs font-medium">Email</button>
                          </>
                        )}
                        {inv.status !== "paid" && inv.status !== "draft" && <button onClick={() => setViewingInvoiceId(inv.id)} className="flex items-center gap-1 bg-card2 rounded-lg px-2.5 py-1.5 text-xs font-medium" style={{ color: "var(--brass)" }}><Coins size={12} /> Payment</button>}
                        {(inv.status === "paid" || partial) && <button onClick={() => setViewingInvoiceId(inv.id)} className="flex items-center gap-1 bg-card2 rounded-lg px-2.5 py-1.5 text-xs font-medium" style={{ color: "var(--sage)" }}><Receipt size={12} /> Receipt</button>}
                        <button onClick={() => { startEditInvoice(inv); setView("addInvoice"); }} className="opacity-60"><Pencil size={13} /></button>
                        <button onClick={() => deleteInvoice(inv.id)} className="opacity-40"><Trash2 size={13} /></button>
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        {["draft", "sent", "paid", "overdue"].map((s) => (
                          <button key={s} onClick={() => setInvoiceStatus(inv.id, s)} className="flex-1 rounded-md py-1 text-xs font-medium" style={{ background: inv.status === s ? "var(--brass)" : "var(--card2)", color: inv.status === s ? "var(--ink)" : "var(--ivory-dim)" }}>
                            {s[0].toUpperCase() + s.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {view === "addInvoice" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setView("invoices")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">{editingInvoiceId ? "Edit invoice" : "New invoice"}</h2>
            </div>

            <div className="flex flex-col gap-3 mb-4">
              <div>
                <label className="text-xs text-ivory-dim">Client</label>
                <select value={invoiceClientId} onChange={(e) => pickInvoiceClient(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm mt-1" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }}>
                  <option value="">No client on file</option>
                  {active.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-ivory-dim">Invoice number</label>
                <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="v-mono w-full rounded-lg px-3 py-2 text-sm mt-1" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-ivory-dim">Issue date</label>
                  <input value={invoiceIssueDate} onChange={(e) => setInvoiceIssueDate(e.target.value)} type="date" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-ivory-dim">Due date</label>
                  <input value={invoiceDueDate} onChange={(e) => setInvoiceDueDate(e.target.value)} type="date" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs text-ivory-dim">Bill-to details (optional)</label>
                <textarea value={invoiceBillTo} onChange={(e) => setInvoiceBillTo(e.target.value)} rows={2} placeholder={"Address, email, PO number…"} className="w-full rounded-lg px-3 py-2 text-sm mt-1" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }} />
              </div>
              <div className="flex gap-3 items-end">
                <div className="w-32">
                  <label className="text-xs text-ivory-dim">{active.business?.taxLabel || "Tax"} %</label>
                  <input value={invoiceTaxRate} onChange={(e) => setInvoiceTaxRate(e.target.value)} type="number" placeholder="0" className="v-mono w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
                {!(active.business?.name || active.business?.logo) && <button onClick={() => setView("bizDetails")} className="text-xs text-brass font-medium pb-2.5">Add your logo &amp; details →</button>}
              </div>
            </div>

            <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Line items</div>
            <datalist id="activity-options">
              {[...new Set([...(active.business?.activities || []), ...(active.invoices || []).flatMap((i) => (i.items || []).map((it) => (it.activity || "").trim()))].filter(Boolean))].slice(0, 60).map((a) => <option key={a} value={a} />)}
            </datalist>
            <div className="flex flex-col gap-2 mb-3">
              {invoiceItems.map((it) => (
                <div key={it.id} className="bg-card rounded-xl p-3">
                  <div className="flex gap-2 mb-2">
                    <input value={it.activity || ""} list="activity-options" onChange={(e) => updateInvoiceItem(it.id, "activity", e.target.value)} placeholder="Activity" className="w-2/5 min-w-0 rounded-lg px-3 py-2 text-sm" />
                    <input value={it.description} onChange={(e) => updateInvoiceItem(it.id, "description", e.target.value)} placeholder="Description" className="flex-1 min-w-0 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input value={it.qty} onChange={(e) => updateInvoiceItem(it.id, "qty", e.target.value)} type="number" placeholder="Qty" className="v-mono w-16 rounded-lg px-2 py-1.5 text-xs" />
                    <span className="text-xs text-ivory-dim">×</span>
                    <input value={it.rate} onChange={(e) => updateInvoiceItem(it.id, "rate", e.target.value)} type="number" placeholder="Rate" className="v-mono flex-1 rounded-lg px-2 py-1.5 text-xs" />
                    <span className="v-mono text-xs shrink-0">{fmt((Number(it.qty) || 0) * (Number(it.rate) || 0))}</span>
                    <button onClick={() => removeInvoiceItem(it.id)} className="opacity-40 shrink-0"><X size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <button onClick={addInvoiceItem} className="text-xs text-brass font-medium">+ Add line</button>
              {unbilledHoursFor(invoiceClientId).length > 0 && (
                <button onClick={addLoggedHoursToInvoice} className="text-xs text-brass font-medium">+ Add {unbilledHoursFor(invoiceClientId).length} unbilled logged {unbilledHoursFor(invoiceClientId).length === 1 ? "shift" : "shifts"}</button>
              )}
            </div>

            <div className="mb-4">
              <label className="text-xs text-ivory-dim">Notes (optional)</label>
              <input value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} placeholder="Payment terms, thank-you note, etc." className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
            </div>

            {(() => {
              const t = invoiceTotals({ items: invoiceItems.map((it) => ({ qty: Number(it.qty) || 0, rate: Number(it.rate) || 0 })), taxRate: invoiceTaxRate });
              return (
                <div className="card-hero rounded-2xl p-4 mb-4">
                  {t.taxRate > 0 && (
                    <>
                      <div className="flex justify-between text-xs text-ivory-dim mb-1"><span>Subtotal</span><span className="v-mono">{fmt(t.subtotal)}</span></div>
                      <div className="flex justify-between text-xs text-ivory-dim mb-2"><span>{active.business?.taxLabel || "Tax"} ({t.taxRate}%)</span><span className="v-mono">{fmt(t.tax)}</span></div>
                    </>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-ivory-dim">Total</span>
                    <span className="v-mono text-xl font-medium">{fmt(t.total)}</span>
                  </div>
                </div>
              );
            })()}

            {!editingInvoiceId && (
              <div className="bg-card rounded-xl p-3.5 mb-5">
                <label className="text-xs text-ivory-dim">Repeat this invoice</label>
                <select value={invoiceRepeat} onChange={(e) => setInvoiceRepeat(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm mt-1" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }}>
                  <option value="none">Don't repeat</option>
                  <option value="weekly">Every week</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Every month</option>
                  <option value="quarterly">Every quarter</option>
                  <option value="yearly">Every year</option>
                </select>
                {invoiceRepeat !== "none" && <div className="text-xs text-ivory-dim mt-2">A new draft is created automatically on each due date. Manage them under Business → Recurring invoices.</div>}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={openDraftReview} className="flex-1 bg-card2 rounded-full py-3 text-sm font-medium">Preview</button>
              <button onClick={saveInvoice} className="flex-[2] bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm">{editingInvoiceId ? "Save changes" : "Create invoice"}</button>
            </div>
          </div>
        )}

        {clientSheet && active && (
          <div className="overlay-backdrop fixed inset-0 z-40 flex items-end lg:items-center justify-center" onClick={() => setClientSheet(null)}>
            <div className="bg-card2 rounded-t-3xl lg:rounded-3xl w-full max-w-sm md:max-w-xl p-5" style={{ maxHeight: "94vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="v-display text-lg font-semibold">{clientSheet.id ? "Edit client" : "Add client"}</h3>
                <button onClick={() => setClientSheet(null)} aria-label="Close"><X size={18} className="text-ivory-dim" /></button>
              </div>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs text-ivory-dim">Name</label>
                  <input autoFocus value={clientSheet.name} onChange={(e) => setClientSheet({ ...clientSheet, name: e.target.value })} placeholder="Client or project name" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs text-ivory-dim">Email (optional)</label>
                  <input value={clientSheet.email} onChange={(e) => setClientSheet({ ...clientSheet, email: e.target.value })} type="email" inputMode="email" placeholder="client@example.com" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs text-ivory-dim">Phone (optional)</label>
                  <input value={clientSheet.phone} onChange={(e) => setClientSheet({ ...clientSheet, phone: e.target.value })} type="tel" inputMode="tel" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs text-ivory-dim">Address (optional)</label>
                  <textarea value={clientSheet.address} onChange={(e) => setClientSheet({ ...clientSheet, address: e.target.value })} rows={3} placeholder="Shown under “Billed to” on invoices" className="w-full rounded-lg px-3 py-2 text-sm mt-1" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }} />
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => setClientSheet(null)} className="flex-1 bg-card rounded-full py-2.5 text-sm font-medium">Cancel</button>
                <button onClick={saveClient} disabled={!clientSheet.name.trim()} className="flex-[2] bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm disabled:opacity-40">{clientSheet.id ? "Save changes" : "Add client"}</button>
              </div>
            </div>
          </div>
        )}

        {viewingInvoiceId && active && (() => {
          const inv = (active.invoices || []).find((i) => i.id === viewingInvoiceId);
          if (!inv) return null;
          const client = active.clients.find((c) => c.id === inv.clientId);
          return (
            <InvoiceReview
              inv={inv} client={client} profile={active} fmt={fmt}
              onClose={() => { quickPrintRef.current = false; setViewingInvoiceId(null); }}
              onEdit={() => { startEditInvoice(inv); setViewingInvoiceId(null); setView("addInvoice"); }}
              onMarkSent={() => setInvoiceStatus(inv.id, "sent")}
              onSavePaidMethod={(m) => setInvoicePaidMethod(inv.id, m)}
              onRecordPayment={(pay) => recordInvoicePayment(inv.id, pay)}
              onRemovePayment={(payId) => removeInvoicePayment(inv.id, payId)}
              onSaveEmail={(email) => updateActive((p) => ({ ...p, clients: p.clients.map((c) => (c.id === inv.clientId ? { ...c, email } : c)) }))}
              onOpenDetails={() => { setViewingInvoiceId(null); setView("bizDetails"); }}
              onOpenPayments={() => { setViewingInvoiceId(null); setView("bizPayments"); }}
              ctx={ctx}
            />
          );
        })()}

        {showCountry && active && (
          <CountrySheet showTax={active.type === "business"} currencies={CURRENCIES} current={userCountry} currentCurrency={active.currency || "USD"} hasEntries={profiles.some((p) => (p.expenses || []).length > 0 || (p.invoices || []).length > 0)} onSave={saveCountry} onSkip={() => { setShowCountry(false); setCountrySkipped(true); }} />
        )}

        {reviewDraft && active && (
          <InvoiceReview
            inv={reviewDraft} isDraft client={active.clients.find((c) => c.id === reviewDraft.clientId)} profile={active} fmt={fmt}
            onClose={() => setReviewDraft(null)}
            onSave={() => { setReviewDraft(null); saveInvoice(); }}
            onOpenDetails={() => { setReviewDraft(null); setView("bizDetails"); }}
            ctx={ctx}
          />
        )}

        {view === "bizDetails" && active && <BusinessDetailsView active={active} updateActive={updateActive} setView={setView} showToast={showToast} />}
        {view === "payslips" && active && <PayslipsView active={active} updateActive={updateActive} fmt={fmt} setView={setView} showToast={showToast} ctx={ctx} />}
        {view === "taxHelp" && active && <TaxHelp active={active} updateActive={updateActive} fmt={fmt} setView={setView} showToast={showToast} country={userCountry} onChangeCountry={() => setShowCountry(true)} />}
        {view === "bizPayments" && active && <PaymentsView active={active} updateActive={updateActive} ctx={ctx} setView={setView} showToast={showToast} />}
        {view === "bizPL" && active && <PLView active={active} updateActive={updateActive} fmt={fmt} setView={setView} showToast={showToast} />}
        {view === "mileage" && active && <MileageView active={active} updateActive={updateActive} fmt={fmt} setView={setView} showToast={showToast} />}
        {view === "hours" && active && <HoursView active={active} updateActive={updateActive} fmt={fmt} setView={setView} showToast={showToast} />}
        {view === "recurringInvoices" && active && <RecurringInvoicesView active={active} updateActive={updateActive} fmt={fmt} setView={setView} onNew={() => { resetInvoiceForm({ repeat: "monthly" }); setView("addInvoice"); }} />}

        {view === "categories" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("settings")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Categories</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Rename or remove a category to match how "{active.name}" spends. Renaming updates past entries too.</p>

            <div className="flex flex-col gap-2 mb-6">
              {active.categories.map((c) => (
                <div key={c} className="flex items-center gap-3 bg-card rounded-xl px-3 py-2.5">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: categoryColor(c) }} />
                  <div className="flex-1 text-sm truncate">{c}</div>
                  <button onClick={() => startEditCategory(c)} className="opacity-60 shrink-0"><Pencil size={14} /></button>
                  <button onClick={() => deleteCategory(c)} className="opacity-40 shrink-0"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            <div className="bg-card2 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">{editingCategoryOriginal ? "Rename category" : "New category"}</div>
              <div className="flex gap-2">
                <input value={categoryDraft} onChange={(e) => setCategoryDraft(e.target.value)} placeholder="Category name" className="flex-1 rounded-lg px-3 py-2 text-sm" />
                <button onClick={saveCategory} className="bg-gold-grad text-ink font-semibold rounded-lg px-4 text-sm">{editingCategoryOriginal ? "Save" : "Add"}</button>
                {editingCategoryOriginal && <button onClick={resetCategoryForm} className="text-xs text-ivory-dim px-1">Cancel</button>}
              </div>
            </div>
          </div>
        )}

        {view === "account" && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("settings")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Account & sync</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Optional. Everything still works fully offline with no account at all — this just lets your data follow you to another device.</p>

            {!syncConfigured ? (
              <div>
                {configWarning && (
                  <div className="rounded-2xl p-4 mb-4 text-xs leading-relaxed" style={{ background: "rgba(181,68,95,0.15)", border: "1px solid var(--rust)" }}>
                    <span className="font-semibold" style={{ color: "var(--rust)" }}>Setup problem: </span>
                    <span className="text-ivory-dim">{configWarning}</span>
                  </div>
                )}
                <div className="bg-card2 rounded-2xl p-4 mb-5 text-xs text-ivory-dim leading-relaxed">
                  <span className="text-ivory font-semibold">Sync isn't connected yet.</span> This is expected until you set it up — it takes a free Supabase project and two values pasted into <span className="v-mono text-brass">src/supabaseClient.js</span> (the README walks through it step by step). Nothing else in the app is affected; everything works fully offline without this.
                </div>
                <div className="opacity-30 pointer-events-none select-none">
                  <div className="flex gap-2 mb-5">
                    <div className="flex-1 rounded-lg py-2 text-xs font-medium text-center" style={{ background: "var(--brass)", color: "var(--ink)" }}>Log in</div>
                    <div className="flex-1 rounded-lg py-2 text-xs font-medium text-center" style={{ background: "var(--card)", color: "var(--ivory-dim)" }}>Sign up</div>
                  </div>
                  <div className="flex flex-col gap-3 mb-3">
                    <div className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: "var(--card)", color: "var(--ivory-dim)" }}>Email</div>
                    <div className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: "var(--card)", color: "var(--ivory-dim)" }}>Password</div>
                  </div>
                  <div className="w-full text-center rounded-full py-2.5 text-sm font-semibold" style={{ background: "var(--brass)", color: "var(--ink)" }}>Log in</div>
                </div>
              </div>
            ) : session ? (
              <div>
                <div className="bg-card rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <UserCircle size={16} className="text-brass" />
                    <span className="text-sm font-semibold">{session.user.email}</span>
                  </div>
                  {session.user.user_metadata?.phone && (
                    <div className="text-xs text-ivory-dim ml-6">{session.user.user_metadata.phone}</div>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: accountPlan === "paid" ? "var(--brass-dim)" : "var(--card2)", color: accountPlan === "paid" ? "var(--brass)" : "var(--ivory-dim)" }}>
                      {accountPlan === "paid" ? "Paid" : "Free"}
                    </span>
                    <span className="text-xs text-ivory-dim flex items-center gap-1">
                      <Cloud size={12} />
                      {syncStatus === "syncing" ? "Syncing…" : syncStatus === "error" ? "Sync error, will retry" : syncStatus === "locked" || syncLock ? "Locked: enter your passphrase" : "Synced"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 mt-3">
                    <span className="text-xs text-ivory-dim">{lastSynced ? `Last checked ${lastSynced.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "Not checked yet"}</span>
                    <button onClick={() => syncNow(true)} disabled={syncStatus === "syncing"} className="flex items-center gap-1.5 bg-card2 rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50">
                      <RefreshCw size={12} className={syncStatus === "syncing" ? "animate-spin" : ""} /> Sync now
                    </button>
                  </div>
                  <p className="text-[11px] text-ivory-dim mt-3 leading-relaxed">Changes you make are sent straight away. Other devices that have the app open usually show them within a few seconds, and always within about 25 seconds. A device that was closed or offline catches up as soon as you open it or it reconnects.</p>
                </div>
                <EncryptionCard on={encOn} locked={!!syncLock} hasBusiness={profiles.some((p) => p.type === "business")} onEnable={handleEnableEncryption} onDisable={handleDisableEncryption} onUnlock={() => setSyncPrompt(true)} onForgot={() => setShowForgotPass(true)} />
                <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-1.5 bg-card2 rounded-full py-2.5 text-sm font-medium">
                  <LogOut size={14} /> Sign out
                </button>
                <p className="text-xs text-ivory-dim mt-3 text-center">Signing out doesn't delete anything — your data stays in your account and on this device.{encOn ? " It does forget your encryption key on this device." : ""}</p>

                <div className="mt-8 pt-5 border-t border-hair">
                  <div className="text-xs font-semibold mb-1" style={{ color: "var(--rust)" }}>Delete account</div>
                  <p className="text-xs text-ivory-dim leading-relaxed mb-3">Permanently deletes your login and everything stored online for it. Data on this device stays unless you choose to erase it too.</p>
                  <button onClick={() => setShowDelete(true)} className="w-full rounded-full py-2.5 text-sm font-medium bg-card2" style={{ color: "var(--rust)" }}>Delete my account</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-5">
                  <button onClick={() => { setAuthMode("login"); setAuthError(""); }} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: authMode === "login" ? "var(--brass)" : "var(--card)", color: authMode === "login" ? "var(--ink)" : "var(--ivory-dim)" }}>Log in</button>
                  <button onClick={() => { setAuthMode("signup"); setAuthError(""); }} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: authMode === "signup" ? "var(--brass)" : "var(--card)", color: authMode === "signup" ? "var(--ink)" : "var(--ivory-dim)" }}>Sign up</button>
                </div>

                <div className="flex flex-col gap-3 mb-2">
                  <input value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} type="email" autoComplete="email" placeholder="Email" className="w-full rounded-lg px-3 py-2 text-sm" />
                  {authMode === "signup" && (
                    <input value={authPhone} onChange={(e) => setAuthPhone(e.target.value)} type="tel" placeholder="Phone number" className="w-full rounded-lg px-3 py-2 text-sm" />
                  )}
                  {authMode !== "forgot" && (
                    <input value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} type="password" autoComplete={authMode === "signup" ? "new-password" : "current-password"} placeholder="Password" className="w-full rounded-lg px-3 py-2 text-sm" />
                  )}
                </div>

                {authMode === "signup" && (
                  <ul className="mb-3 space-y-1" aria-label="Password requirements">
                    {passwordChecks(authPassword).map((c) => (
                      <li key={c.id} className="flex items-center gap-2 text-xs" style={{ color: c.ok ? "var(--sage)" : "var(--ivory-dim)" }}>
                        {c.ok ? <CheckIcon size={13} /> : <Circle size={13} />} {c.label}
                      </li>
                    ))}
                  </ul>
                )}

                {authMode === "signup" && (
                  <label className="flex items-start gap-2 text-xs mb-3 leading-relaxed">
                    <input type="checkbox" checked={authAgree} onChange={(e) => setAuthAgree(e.target.checked)} className="mt-0.5 shrink-0" />
                    <span>I agree to the{" "}
                      <button type="button" onClick={() => setLegalDoc({ doc: "terms", full: true })} className="text-brass font-medium underline">Terms &amp; Conditions</button>
                      {" "}and have read the{" "}
                      <button type="button" onClick={() => setLegalDoc({ doc: "privacy", full: true })} className="text-brass font-medium underline">Privacy Policy</button>.
                    </span>
                  </label>
                )}

                {authMode === "login" && (
                  <button onClick={() => { setAuthMode("forgot"); setAuthError(""); setAuthInfo(""); }} className="text-xs text-brass font-medium underline mb-3">Forgot password?</button>
                )}
                {authMode === "forgot" && (
                  <p className="text-xs text-ivory-dim leading-relaxed mb-3">Enter your email and we'll send a link to choose a new password.</p>
                )}

                {authError && <div className="text-xs text-rust mb-3">{authError}</div>}
                {authInfo && <div className="text-xs mb-3 leading-relaxed" style={{ color: "var(--sage)" }}>{authInfo}</div>}

                <button onClick={authMode === "signup" ? handleSignUp : authMode === "forgot" ? handleForgotPassword : handleSignIn} disabled={authLoading} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm disabled:opacity-50">
                  {authLoading ? "One moment…" : authMode === "signup" ? "Create account" : authMode === "forgot" ? "Email me a reset link" : "Log in"}
                </button>
                {authMode === "forgot" && (
                  <button onClick={() => { setAuthMode("login"); setAuthError(""); setAuthInfo(""); }} className="w-full mt-2 py-2 text-xs text-ivory-dim">Back to log in</button>
                )}

                <p className="text-xs text-ivory-dim mt-4 leading-relaxed">
                  {authMode === "signup"
                    ? "If you already have data on this device, it becomes your account's first synced copy automatically."
                    : authMode === "forgot"
                      ? "Resetting your password doesn't touch your data. If you turned on end-to-end encryption, your encryption passphrase is separate and can't be reset."
                      : "If your account already has data saved, we'll ask before replacing what's on this device."}
                </p>
              </div>
            )}
          </div>
        )}

        {view === "install" && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("settings")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Install app</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Puts it on your home screen or Start menu with its own icon, and lets it work offline like a regular app — it's still the same website underneath, just installed.</p>

            <AppUpdateCard />
            {isStandalone ? (
              <div className="bg-card rounded-2xl p-4 flex gap-3">
                <Check size={18} className="text-sage shrink-0" />
                <div>
                  <div className="text-sm font-semibold">Already installed here</div>
                  <div className="text-xs text-ivory-dim mt-1">This device is running the installed version. To add it on another device, open the same link there.</div>
                </div>
              </div>
            ) : installPromptEvent ? (
              <div className="bg-card rounded-2xl p-4 mb-5">
                <div className="text-sm font-semibold mb-2">This device supports one-tap install</div>
                <button onClick={triggerInstall} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm">Install now</button>
              </div>
            ) : (
              <div className="bg-card2 rounded-xl p-3 mb-5 text-xs text-ivory-dim">This browser hasn't offered an automatic install button yet — use the instructions for your device below instead.</div>
            )}

            <div className="flex flex-col gap-3">
              <div className="bg-card rounded-2xl p-4">
                <div className="text-sm font-semibold mb-1.5">Android</div>
                <div className="text-xs text-ivory-dim leading-relaxed">Open this page in Chrome → tap the three-dot menu → "Install app." Or use the "Install now" button above if it's showing.</div>
              </div>
              <div className="bg-card rounded-2xl p-4">
                <div className="text-sm font-semibold mb-1.5">iPhone / iPad</div>
                <div className="text-xs text-ivory-dim leading-relaxed">Must be done in Safari (not Chrome) — iOS only allows installs from Safari. Tap the Share icon, scroll down, tap "Add to Home Screen," then "Add."</div>
              </div>
              <div className="bg-card rounded-2xl p-4">
                <div className="text-sm font-semibold mb-1.5">Windows / Mac</div>
                <div className="text-xs text-ivory-dim leading-relaxed">Open this page in Chrome or Edge → look for an install icon in the address bar, or the three-dot menu → "Install." It opens in its own window from then on.</div>
              </div>
            </div>
          </div>
        )}

        {view === "guide" && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("settings")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Getting started</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">A quick tour of the less obvious features — everything here is reachable from the Tally tab or Settings.</p>

            <div className="flex flex-col gap-3">
              {[
                { icon: Users, title: "Profiles: Personal, Household, Business", body: "Switch profiles from the pill at the top of Tally, or add another from Settings. Each one has its own currency, categories, budgets, and data — a household and a business you run can live side by side without mixing." },
                { icon: Tag, title: "Categories", body: "Settings → Manage categories. Add your own, rename one (updates every past entry that used it), or delete one you don't need. Every profile starts with a sensible default set you're free to change." },
                { icon: Wallet, title: "Envelope budgets", body: "Settings → Envelope budgets. Give any category its own monthly limit — separate from the single overall budget on the dashboard — so you can see \"how much left for dining\" specifically, not just the total." },
                { icon: Repeat, title: "Recurring expenses", body: "Settings → Recurring expenses. Set up rent, a subscription, anything on a schedule, and it logs itself automatically when due — including catching up if you didn't open the app for a while." },
                { icon: Target, title: "Savings goals", body: "Settings → Savings goals. Name a goal, set a target and optional date, then log contributions as you save toward it. Shows up combined with your Invest portfolio as a net worth figure." },
                { icon: Pencil, title: "Editing an entry", body: "Tap the pencil icon next to any expense in Tally to change its amount, category, date, or any custom field — not just delete and re-add it." },
                { icon: Coins, title: "Currency per profile", body: "Settings → Currency. Each profile picks its own — a household in rupees and a business in dollars can coexist. Each holding is priced in the currency it's listed in (BHP.AX in A$, FPH.NZ in NZ$) and converted to your profile's currency at a live rate." },
                { icon: TrendingUp, title: "Chronicle", body: "Week, month, or year view with a calendar heatmap and category breakdowns — use the arrows either side of the total to step back through past periods, not just see the current one." },
                { icon: LineChart, title: "Invest", body: "Build your net worth from accounts, property and debts, with a snapshot every week or month. Record every buy, sale and dividend to see annualised return against the S&P 500, spread your money across asset classes, sectors and regions, and set price alerts on your watchlist. The loan calculator can send a mortgage straight to your debts." },
                { icon: Upload, title: "Import from a bank file", body: "Settings → Import expenses. Drop in a CSV, Excel or PDF statement and it works out the dates, amounts, merchants and categories by itself — no column picking. It skips transfers and anything already in your ledger, learns from how you categorize, and flags repeating costs for you to confirm." },
                { icon: Briefcase, title: "Business tools", body: "On a Business profile, the Business tab has invoices with your logo and details, recurring invoices, a mileage log, staff hours (which can be added straight onto an invoice), and a profit & loss view by month, quarter or year." },
                { icon: Search, title: "Search your ledger", body: "The search bar above Tally's entry list filters by note or category — useful once you've logged more than a screenful of expenses." },
                { icon: Download, title: "Backup & restore", body: "Settings → Backup & restore. Exports everything as one file you can keep, and loads it back in on request — the manual, no-account way to move your data to a new device." },
                { icon: Cloud, title: "Account & sync (optional)", body: "Entirely opt-in — the app works fully offline forever with no account. Sign up only if you want your data to follow you across devices automatically." },
              ].map((item, i) => (
                <div key={i} className="bg-card rounded-2xl p-4 flex gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--brass-dim)" }}>
                    <item.icon size={14} className="text-brass" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{item.title}</div>
                    <div className="text-xs text-ivory-dim mt-1 leading-relaxed">{item.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "budgets" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Envelope budgets</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Give each category its own monthly allowance. Leave blank for no limit.</p>
            <div className="flex flex-col gap-3">
              {active.categories.map((c) => {
                const spent = monthSpendByCategory[c] || 0;
                const budget = active.categoryBudgets?.[c];
                const pct = budget ? Math.min(100, (spent / budget) * 100) : 0;
                return (
                  <div key={c} className="bg-card rounded-xl p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: categoryColor(c) }} />
                        <span className="text-sm">{c}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-ivory-dim">{currencySymbol}</span>
                        <input
                          type="number"
                          defaultValue={budget || ""}
                          onBlur={(e) => setCategoryBudget(c, e.target.value)}
                          placeholder="—"
                          className="rounded-md px-2 py-1 text-xs w-16 text-right v-mono"
                        />
                      </div>
                    </div>
                    {budget ? (
                      <>
                        <div className="w-full h-1.5 rounded-full bg-card2 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: spent > budget ? "var(--rust)" : "var(--brass)" }} />
                        </div>
                        <div className="text-xs text-ivory-dim mt-1">
                          {spent > budget ? <span className="text-rust">{fmt(spent - budget)} over</span> : <span>{fmt(budget - spent)} left</span>} of {fmt(budget)}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-ivory-dim">{fmt(spent)} spent this month</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "recurring" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Recurring expenses</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Rent, subscriptions, anything on a schedule. Balance & Beyond logs each one automatically when it's due.</p>

            <div className="flex flex-col gap-2 mb-6">
              {active.recurring.map((r) => (
                <div key={r.id} className="flex items-center gap-3 bg-card rounded-xl px-3 py-2.5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--brass-dim)" }}>
                    <Repeat size={14} className="text-brass" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.category}{r.note ? ` · ${r.note}` : ""}</div>
                    <div className="text-xs text-ivory-dim">{r.frequency === "weekly" ? "Weekly" : "Monthly"} · next {r.nextDate}</div>
                  </div>
                  <div className="v-mono text-xs shrink-0">{fmt(r.amount)}</div>
                  <button onClick={() => startEditRecurring(r)} className="opacity-60 shrink-0"><Pencil size={14} /></button>
                  <button onClick={() => deleteRecurring(r.id)} className="opacity-40 shrink-0"><Trash2 size={14} /></button>
                </div>
              ))}
              {active.recurring.length === 0 && <div className="text-ivory-dim text-sm text-center py-6">No recurring expenses yet.</div>}
            </div>

            <div className="bg-card2 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">{editingRecurringId ? "Edit recurring expense" : "New recurring expense"}</div>
              <div className="flex items-center gap-1 mb-3">
                <span className="v-mono text-xl text-ivory-dim">{currencySymbol}</span>
                <input value={recurringAmount} onChange={(e) => setRecurringAmount(e.target.value)} type="number" placeholder="0.00" className="v-mono text-xl flex-1 rounded-lg px-2 py-1" />
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {getCategories(active).map((c) => (
                  <button key={c} onClick={() => setRecurringCategory(c)} className="rounded-lg py-2 text-xs font-medium text-center" style={{ background: recurringCategory === c ? "var(--brass)" : "var(--card)", color: recurringCategory === c ? "var(--ink)" : "var(--ivory-dim)" }}>{c}</button>
                ))}
              </div>
              <input value={recurringNote} onChange={(e) => setRecurringNote(e.target.value)} placeholder="Note (optional)" className="w-full rounded-lg px-3 py-2 text-sm mb-3" />
              <div className="flex gap-2 mb-3">
                <button onClick={() => setRecurringFrequency("weekly")} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: recurringFrequency === "weekly" ? "var(--brass)" : "var(--card)", color: recurringFrequency === "weekly" ? "var(--ink)" : "var(--ivory-dim)" }}>Weekly</button>
                <button onClick={() => setRecurringFrequency("monthly")} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: recurringFrequency === "monthly" ? "var(--brass)" : "var(--card)", color: recurringFrequency === "monthly" ? "var(--ink)" : "var(--ivory-dim)" }}>Monthly</button>
              </div>
              <div className="mb-3">
                <label className="text-xs text-ivory-dim">Starts</label>
                <input value={recurringStartDate} onChange={(e) => setRecurringStartDate(e.target.value)} type="date" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveRecurring} className="flex-1 bg-gold-grad text-ink font-semibold rounded-lg py-2 text-sm">{editingRecurringId ? "Save changes" : "Add recurring"}</button>
                {editingRecurringId && <button onClick={resetRecurringForm} className="px-4 text-xs text-ivory-dim">Cancel</button>}
              </div>
            </div>

            <BillsSection key={active.id} active={active} fmt={fmt} session={session} />
          </div>
        )}

        {view === "goals" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Savings goals</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Give a goal a name and a target, then log contributions as you save toward it.</p>

            <div className="flex flex-col gap-3 mb-6">
              {active.goals.map((g) => {
                const pct = g.target > 0 ? Math.min(100, (g.saved / g.target) * 100) : 0;
                return (
                  <div key={g.id} className="bg-card rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Target size={14} className="text-brass" />
                        <span className="text-sm font-semibold">{g.name}</span>
                      </div>
                      <button onClick={() => deleteGoal(g.id)} className="opacity-40"><Trash2 size={13} /></button>
                    </div>
                    <div className="v-mono text-xs text-ivory-dim mb-2">{fmt(g.saved)} of {fmt(g.target)}{g.targetDate ? ` · by ${g.targetDate}` : ""}</div>
                    <div className="w-full h-1.5 rounded-full bg-card2 overflow-hidden mb-3">
                      <div className="h-full rounded-full bg-gold-grad" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex gap-2">
                      <input value={contributionDrafts[g.id] || ""} onChange={(e) => setContributionDrafts((d) => ({ ...d, [g.id]: e.target.value }))} type="number" placeholder="Add funds" className="flex-1 rounded-lg px-3 py-1.5 text-xs" />
                      <button onClick={() => contributeToGoal(g.id)} className="bg-card2 rounded-lg px-3 text-xs font-medium text-brass">Add</button>
                    </div>
                  </div>
                );
              })}
              {active.goals.length === 0 && <div className="text-ivory-dim text-sm text-center py-6">No goals yet.</div>}
            </div>

            <div className="bg-card2 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">New goal</div>
              <input value={goalName} onChange={(e) => setGoalName(e.target.value)} placeholder="Goal name, e.g. Trip to Paris" className="w-full rounded-lg px-3 py-2 text-sm mb-3" />
              <div className="flex gap-2 mb-3">
                <input value={goalTarget} onChange={(e) => setGoalTarget(e.target.value)} type="number" placeholder="Target amount" className="flex-1 rounded-lg px-3 py-2 text-sm" />
                <input value={goalDate} onChange={(e) => setGoalDate(e.target.value)} type="date" className="flex-1 rounded-lg px-3 py-2 text-sm" />
              </div>
              <button onClick={addGoal} className="w-full bg-gold-grad text-ink font-semibold rounded-lg py-2 text-sm">Create goal</button>
            </div>
          </div>
        )}

        {view === "import" && active && (
          <ImportView active={active} updateActive={updateActive} fmt={fmt} setView={setView} showToast={showToast} session={session} accountPlan={accountPlan} categoryColor={categoryColor} />
        )}

        {view === "fields" && active && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("settings")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Expense fields</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Add extra fields to match how "{active.name}" tracks spending — payment method, location, invoice number, anything you need.</p>

            <div className="flex flex-col gap-2 mb-6">
              {active.customFields.map((f) => (
                <div key={f.id} className="flex items-center gap-3 bg-card rounded-xl px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{f.label}</div>
                    <div className="text-xs text-ivory-dim truncate">{FIELD_TYPES.find((t) => t.id === f.type)?.label}{f.type === "choice" && f.options.length ? `: ${f.options.join(", ")}` : ""}</div>
                  </div>
                  <button onClick={() => startEditField(f)} className="opacity-60 shrink-0"><Pencil size={14} /></button>
                  <button onClick={() => deleteField(f.id)} className="opacity-40 shrink-0"><Trash2 size={14} /></button>
                </div>
              ))}
              {active.customFields.length === 0 && <div className="text-ivory-dim text-sm text-center py-6">No custom fields yet.</div>}
            </div>

            <div className="bg-card2 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">{editingFieldId ? "Edit field" : "New field"}</div>
              <input value={fieldLabel} onChange={(e) => setFieldLabel(e.target.value)} placeholder="Field name, e.g. Payment method" className="w-full rounded-lg px-3 py-2 text-sm mb-3" />
              <div className="grid grid-cols-4 gap-2 mb-3">
                {FIELD_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setFieldType(t.id)} className="rounded-lg py-2 text-xs font-medium text-center" style={{ background: fieldType === t.id ? "var(--brass)" : "var(--card)", color: fieldType === t.id ? "var(--ink)" : "var(--ivory-dim)" }}>{t.label}</button>
                ))}
              </div>
              {fieldType === "choice" && (
                <input value={fieldOptions} onChange={(e) => setFieldOptions(e.target.value)} placeholder="Options, comma separated" className="w-full rounded-lg px-3 py-2 text-sm mb-3" />
              )}
              <div className="flex gap-2">
                <button onClick={saveField} className="flex-1 bg-gold-grad text-ink font-semibold rounded-lg py-2 text-sm">{editingFieldId ? "Save changes" : "Add field"}</button>
                {editingFieldId && <button onClick={resetFieldForm} className="px-4 text-xs text-ivory-dim">Cancel</button>}
              </div>
            </div>
          </div>
        )}

        {view === "tips" && (
          <div className="px-5 pt-8 pb-28">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setView("dashboard")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Quick tips</h2>
            </div>
            <p className="text-ivory-dim text-xs mb-5">Budgeting, saving, investing and keeping your money and privacy safe.</p>
            <div className="flex flex-col gap-3">
              {tipsForProfile.map((t, i) => (
                <div key={i} className="bg-card rounded-2xl p-4 flex gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--brass-dim)" }}>
                    <Lightbulb size={14} className="text-brass" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-brass">{t.topic}</div>
                    <div className="text-sm font-semibold">{t.title}</div>
                    <div className="text-xs text-ivory-dim mt-1 leading-relaxed">{t.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center text-xs text-ivory-dim mt-6">General guidance, not personalized financial advice.</div>
          </div>
        )}

        {view === "privacy" && (
          <PrivacyView onBack={() => setView("settings")} signedIn={!!session} encOn={encOn} lockOn={lockOn} showBusiness={active?.type === "business"} onBackup={exportBackup} onErase={eraseAll} onOpenAccount={() => setView("account")} onOpenLegal={(d) => setLegalDoc({ doc: d, full: false })} />
        )}

        {view === "settings" && (
          <div className="px-5 pt-8 pb-28 wide-cols">
            <div className="flex items-center gap-3 mb-8">
              <button onClick={() => setView(active ? "dashboard" : "onboarding")} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
              <h2 className="v-display text-xl font-semibold">Settings</h2>
            </div>

            <button onClick={() => setView("privacy")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
              <Lock size={18} className="text-brass shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Privacy</div>
                <div className="text-xs text-ivory-dim mt-0.5">Your data stays on this device. See exactly what is and isn't shared.</div>
              </div>
              <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
            </button>

            <LockSettings signedIn={!!session} onBackup={exportBackup} onOpenAccount={() => setView("account")} onLockNow={() => setAppLocked(true)} onChanged={(on) => { setLockOn(on); if (!on) setAppLocked(false); showToast(on ? "App lock is on" : "App lock is off"); }} />

            <div className="bg-card rounded-2xl p-4 mb-3">
              <div className="text-sm font-semibold mb-3">Appearance</div>
              <div className="text-xs text-ivory-dim mb-1.5">Theme</div>
              <Seg label="Theme" value={prefs.theme} onChange={(v) => setPrefs({ theme: v })} options={[["auto", "Auto"], ["light", "Light"], ["dark", "Dark"]]} />
              <div className="text-xs text-ivory-dim mt-1.5">{prefs.theme === "auto" ? `Follows your device. It is ${theme} right now.` : "Fixed on this device."}</div>

              <div className="text-xs text-ivory-dim mt-4 mb-1.5">Text size</div>
              <Seg label="Text size" value={prefs.textSize} onChange={(v) => setPrefs({ textSize: v })} options={[["auto", "Auto"], ["normal", "100%"], ["large", "115%"], ["larger", "130%"], ["largest", "150%"]]} />
              <div className="text-xs text-ivory-dim mt-1.5">{prefs.textSize === "auto" ? `Follows your device's text-size setting. Showing at about ${Math.round(effectiveScale * 100)}%.` : "Fixed on this device, regardless of system settings."}</div>

              {active?.type === "business" && (
              <label className="flex items-start gap-3 mt-4 text-sm cursor-pointer">
                <input type="checkbox" className="mt-1" checked={prefs.reviewInvoices} onChange={(e) => setPrefs({ reviewInvoices: e.target.checked })} />
                <span>Review invoices before printing or emailing<span className="block text-xs text-ivory-dim">Shows a preview with a quick checklist first. Turn off for one-tap Print and Email.</span></span>
              </label>
              )}
              <div className="text-xs text-ivory-dim mt-3">These choices apply to this device only.</div>
            </div>

            <button onClick={() => setView("account")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
              <Cloud size={18} className="text-brass shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Account & sync</div>
                <div className="text-xs text-ivory-dim mt-0.5">{session ? session.user.email : "Optional — sync your data across devices"}</div>
              </div>
              <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
            </button>

            <button onClick={() => setView("install")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
              <Smartphone size={18} className="text-brass shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Install app</div>
                <div className="text-xs text-ivory-dim mt-0.5">{isStandalone ? "Already installed on this device" : "Add it to your home screen"}</div>
              </div>
              <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
            </button>

            <button onClick={() => setView("guide")} className="w-full bg-card rounded-2xl p-4 mb-6 flex gap-3 items-center text-left">
              <BookOpen size={18} className="text-brass shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Getting started</div>
                <div className="text-xs text-ivory-dim mt-0.5">A tour of what this app can do</div>
              </div>
              <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
            </button>

            <button onClick={() => setShowCountry(true)} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
              <span className="text-xl shrink-0" aria-hidden="true">{COUNTRIES[userCountry]?.flag || "🌍"}</span>
              <div className="flex-1">
                <div className="text-xs text-ivory-dim">{active?.type === "business" ? "Country, currency & tax" : "Country & currency"}</div>
                <div className="text-sm font-semibold">{userCountry ? COUNTRIES[userCountry].name : "Not set yet"}{active ? ` · ${(CURRENCIES.find((c) => c.code === (active.currency || "USD")) || { symbol: "$", code: active.currency || "USD" }).symbol} ${active.currency || "USD"}` : ""}</div>
                {!userCountry && active?.type === "business" && <div className="text-xs text-ivory-dim mt-0.5">Tax help follows your country.</div>}
              </div>
              <span className="text-xs text-brass font-medium">Change</span>
            </button>

            {active && (
              <button onClick={() => setView("categories")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
                <Tag size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Manage categories</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Add, rename, or remove categories for "{active.name}"</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {active && (
              <button onClick={() => setView("fields")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
                <SlidersHorizontal size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Manage expense fields</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Add or edit custom fields for "{active.name}"</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {active && active.type !== "business" && (
              <button onClick={() => setView("budgets")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
                <Wallet size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Envelope budgets</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Set a monthly limit per category</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {active && (
              <button onClick={() => setView("recurring")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
                <Repeat size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Recurring expenses</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Auto-log rent, subscriptions, and bills</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {active && active.type !== "business" && (
              <button onClick={() => setView("goals")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
                <Target size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Savings goals</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Track progress toward something specific</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {active && active.type === "business" && (
              <button onClick={() => setView("business")} className="w-full bg-card rounded-2xl p-4 mb-3 flex gap-3 items-center text-left">
                <Briefcase size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Business tools</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Invoices, profit &amp; loss, mileage, staff hours</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {active && (
              <button onClick={() => setView("import")} className="w-full bg-card rounded-2xl p-4 mb-6 flex gap-3 items-center text-left">
                <Upload size={18} className="text-brass shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Import expenses</div>
                  <div className="text-xs text-ivory-dim mt-0.5">Drop in a statement — it reads and sorts itself</div>
                </div>
                <ChevronDown size={15} className="text-ivory-dim -rotate-90 shrink-0" />
              </button>
            )}

            {profiles.length > 0 && (
              <div className="mb-6">
                <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Profiles</div>
                <div className="flex flex-col gap-2">
                  {profiles.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 bg-card rounded-xl px-3 py-2">
                      <input value={p.name} onChange={(e) => renameProfile(p.id, e.target.value)} className="flex-1 bg-transparent text-sm border-none px-0 py-0" style={{ background: "transparent", border: "none" }} />
                      <span className="text-xs text-ivory-dim">{PROFILE_META[p.type].label}</span>
                      <button onClick={() => deleteProfile(p.id)} className="opacity-40"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setView("newProfile")} className="text-xs text-brass font-medium mt-3 flex items-center gap-1">
                  <Plus size={13} /> Add another profile
                </button>
              </div>
            )}

            <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Backup & restore</div>
            <button onClick={exportBackup} className="w-full flex items-center justify-center gap-1.5 bg-card2 rounded-full py-2.5 text-sm font-medium mb-2">
              <Download size={14} /> Export full backup (all profiles)
            </button>
            <label className="w-full flex items-center justify-center gap-1.5 bg-card2 rounded-full py-2.5 text-sm font-medium mb-6 cursor-pointer">
              <Upload size={14} /> Restore from backup
              <input type="file" accept=".json" className="hidden" onChange={(e) => { if (e.target.files[0]) handleRestoreFile(e.target.files[0]); e.target.value = ""; }} />
            </label>

            {active && (
              <button onClick={() => exportCSV(active)} className="w-full flex items-center justify-center gap-1.5 bg-card2 rounded-full py-2.5 text-sm font-medium mb-3">
                <Download size={14} /> Export "{active.name}" tally (CSV)
              </button>
            )}
            <button onClick={eraseAll} className="w-full text-center text-rust text-xs py-2">Erase all data on this device</button>
          </div>
        )}

        {showAdd && active && (
          <div className="overlay-backdrop fixed inset-0 z-40 flex items-end lg:items-center justify-center">
            <div className="bg-card2 rounded-t-3xl lg:rounded-3xl w-full max-w-sm md:max-w-md p-5" style={{ maxHeight: "85vh", overflowY: "auto" }}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="v-display text-lg font-semibold">{editingExpenseId ? "Edit expense" : "Add expense"}</h3>
                <button onClick={() => setShowAdd(false)}><X size={18} className="text-ivory-dim" /></button>
              </div>

              {!editingExpenseId && (
                <ReceiptScan
                  categories={active.categories || []} currency={active.currency} session={session}
                  onResult={(r) => {
                    if (r.total) setAmount(String(r.total));
                    if (r.category) setCategory(r.category);
                    if (r.note) setNote(r.note);
                    if (r.date) setDate(r.date);
                  }}
                />
              )}

              <div className="mb-4">
                <label className="text-xs text-ivory-dim">Amount</label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="v-mono text-2xl text-ivory-dim">{currencySymbol}</span>
                  <input autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="0.00" className="v-mono text-2xl flex-1 rounded-lg px-2 py-1" />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs text-ivory-dim">Category</label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {getCategories(active).map((c) => (
                    <button key={c} onClick={() => setCategory(c)} className="rounded-lg py-2 text-xs font-medium text-center" style={{ background: category === c ? "var(--brass)" : "var(--card)", color: category === c ? "var(--ink)" : "var(--ivory-dim)" }}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {active.type === "household" && (
                <div className="mb-4">
                  <label className="text-xs text-ivory-dim">Who's this for</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <button onClick={() => setAssignMember("shared")} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: assignMember === "shared" ? "var(--brass)" : "var(--card)", color: assignMember === "shared" ? "var(--ink)" : "var(--ivory-dim)" }}>Shared</button>
                    {active.members.map((m) => (
                      <button key={m.id} onClick={() => setAssignMember(m.id)} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: assignMember === m.id ? "var(--brass)" : "var(--card)", color: assignMember === m.id ? "var(--ink)" : "var(--ivory-dim)" }}>{m.name}</button>
                    ))}
                  </div>
                </div>
              )}

              {active.type === "business" && (
                <>
                  <div className="mb-4">
                    <label className="text-xs text-ivory-dim">Client / project</label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      <button onClick={() => setAssignClient("none")} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: assignClient === "none" ? "var(--brass)" : "var(--card)", color: assignClient === "none" ? "var(--ink)" : "var(--ivory-dim)" }}>None</button>
                      {active.clients.map((c) => (
                        <button key={c.id} onClick={() => setAssignClient(c.id)} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: assignClient === c.id ? "var(--brass)" : "var(--card)", color: assignClient === c.id ? "var(--ink)" : "var(--ivory-dim)" }}>{c.name}</button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-xs text-ivory-dim">Tax status</label>
                    <div className="flex gap-2 mt-1.5">
                      <button onClick={() => setDeductible(true)} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: deductible ? "var(--brass)" : "var(--card)", color: deductible ? "var(--ink)" : "var(--ivory-dim)" }}>Deductible</button>
                      <button onClick={() => setDeductible(false)} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: !deductible ? "var(--brass)" : "var(--card)", color: !deductible ? "var(--ink)" : "var(--ivory-dim)" }}>Not deductible</button>
                    </div>
                  </div>
                </>
              )}

              {(active.customFields || []).length > 0 && (
                <div className="mb-4 flex flex-col gap-4">
                  {active.customFields.map((f) => (
                    <div key={f.id}>
                      <label className="text-xs text-ivory-dim">{f.label}</label>
                      {f.type === "text" && (
                        <input value={customValues[f.id] || ""} onChange={(e) => setCustomValues((v) => ({ ...v, [f.id]: e.target.value }))} className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                      )}
                      {f.type === "number" && (
                        <input type="number" value={customValues[f.id] || ""} onChange={(e) => setCustomValues((v) => ({ ...v, [f.id]: e.target.value }))} className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                      )}
                      {f.type === "yesno" && (
                        <div className="flex gap-2 mt-1.5">
                          <button onClick={() => setCustomValues((v) => ({ ...v, [f.id]: "Yes" }))} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: customValues[f.id] === "Yes" ? "var(--brass)" : "var(--card)", color: customValues[f.id] === "Yes" ? "var(--ink)" : "var(--ivory-dim)" }}>Yes</button>
                          <button onClick={() => setCustomValues((v) => ({ ...v, [f.id]: "No" }))} className="flex-1 rounded-lg py-2 text-xs font-medium" style={{ background: customValues[f.id] === "No" ? "var(--brass)" : "var(--card)", color: customValues[f.id] === "No" ? "var(--ink)" : "var(--ivory-dim)" }}>No</button>
                        </div>
                      )}
                      {f.type === "choice" && (
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {(f.options || []).map((opt) => (
                            <button key={opt} onClick={() => setCustomValues((v) => ({ ...v, [f.id]: opt }))} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: customValues[f.id] === opt ? "var(--brass)" : "var(--card)", color: customValues[f.id] === opt ? "var(--ink)" : "var(--ivory-dim)" }}>{opt}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mb-4">
                <label className="text-xs text-ivory-dim">Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was this for? (optional)" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
              </div>

              <div className="mb-6">
                <label className="text-xs text-ivory-dim">Date</label>
                <input value={date} onChange={(e) => setDate(e.target.value)} type="date" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
              </div>

              <button onClick={addExpense} disabled={!amount || Number(amount) <= 0 || !category} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm disabled:opacity-40">
                {editingExpenseId ? "Save changes" : "Add to tally"}
              </button>
            </div>
          </div>
        )}

        {showReset && session && <ResetPasswordModal onDone={() => { setShowReset(false); showToast("Password updated. You're signed in"); }} onCancel={() => setShowReset(false)} />}
        <OldLinkBanner />
        {syncLock && syncPrompt && session && !showForgotPass && <UnlockSyncModal onUnlock={handleUnlockSync} onLater={() => setSyncPrompt(false)} onForgot={() => setShowForgotPass(true)} />}
        {showForgotPass && session && (
          <ForgotPassphraseModal
            profileCount={profiles.length}
            entryCount={profiles.reduce((n, p) => n + (p.expenses?.length || 0), 0)}
            onReplace={handleForgotPassphrase}
            onCancel={() => setShowForgotPass(false)}
          />
        )}
        {legalDoc && <LegalModal doc={legalDoc.doc} showBusiness={legalDoc.full || active?.type === "business"} onClose={() => setLegalDoc(null)} onSwitch={(d) => setLegalDoc({ doc: d, full: legalDoc.full })} />}
        {showDelete && session && <DeleteAccountModal email={session.user.email} hasBusiness={profiles.some((p) => p.type === "business")} onDelete={handleDeleteAccount} onCancel={() => setShowDelete(false)} />}

        <AssistantFab active={active} session={session} hidden={!showTabs || showAdd} lifted={showTabs} onOpenBills={() => setView("recurring")} />

        {showTabs && (
          <nav aria-label="Main" className="tabbar fixed bottom-0 left-0 right-0 z-20 bg-card border-t border-hair">
            <div className="tab-brand">
              <img src="/logo-mark.png" alt="" className="w-8 h-8 rounded-lg" />
              <span className="v-display text-base font-semibold">Balance &amp; Beyond</span>
            </div>
            <div className="tabbar-inner max-w-sm md:max-w-2xl mx-auto flex items-stretch">
              {[
                { id: "dashboard", icon: Wallet, label: "Tally" },
                { id: "insights", icon: TrendingUp, label: "Chronicle" },
                ...(active?.type !== "business" ? [{ id: "invest", icon: LineChart, label: "Invest" }] : []),
                ...(active?.type === "household" ? [{ id: "members", icon: Users, label: "Members" }] : []),
                ...(active?.type === "business" ? [{ id: "business", icon: Briefcase, label: "Business" }] : []),
                { id: "settings", icon: SettingsIcon, label: "Settings" },
              ].map((t) => (
                <button key={t.id} onClick={() => setView(t.id)} data-active={view === t.id} aria-current={view === t.id ? "page" : undefined} className="tabbtn flex-1 flex flex-col items-center gap-1 py-3" style={{ color: view === t.id ? "var(--brass)" : "var(--ivory-dim)" }}>
                  <t.icon size={17} />
                  <span className="tab-label">{t.label}</span>
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}


// A client opening  /#/inv/<token>  sees only that invoice: the rest of the app (and any local data) is never loaded.
function parseInvoiceHash() {
  const m = /^#\/inv\/([\w-]{16,64})(?:\?(.*))?$/.exec(window.location.hash || "");
  return m ? { token: m[1], paid: /(^|&)paid=1(&|$)/.test(m[2] || "") } : null;
}
function parseLegalHash() {
  const m = /^#\/(privacy|terms)\/?$/.exec(window.location.hash || "");
  return m ? { legal: m[1] } : null;
}
function PublicShell({ route }) {
  useAppearance();
  return (
    <div className="vellum-app min-h-screen w-full flex justify-center" style={{ background: "var(--page)" }}>
      <style>{APP_CSS}</style>
      <div className="relative w-full max-w-xl bg-ink text-ivory" style={{ minHeight: "100vh" }}>
        {route.legal ? <LegalPage doc={route.legal} /> : <PublicInvoice token={route.token} paidFlag={route.paid} />}
      </div>
    </div>
  );
}
export default function App() {
  const [route, setRoute] = useState(() => parseInvoiceHash() || parseLegalHash());
  useEffect(() => {
    const on = () => setRoute(parseInvoiceHash() || parseLegalHash());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route ? <PublicShell route={route} /> : <AppMain />;
}
